import { haFlag } from "@/lib/ambito";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { api, vehicleFotoUrl } from "@/lib/api";
import { formatEUR } from "@/lib/utils";
import { useAuth, isElevated } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Save, Copy, Trash2, ArrowLeft, AlertTriangle, Camera, FileText,
  Wallet, Calculator, History, RefreshCcw, Plus, X,
  Search, ScanLine, ListChecks, Globe, Package, Printer, ChevronLeft, ChevronRight,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  VEHICLE_STATO_LABELS, VEHICLE_PROVENIENZA_LABELS,
  VEHICLE_CARBURANTE_LABELS, VEHICLE_CAMBIO_LABELS, VEHICLE_TRAZIONE_LABELS,
  VEHICLE_TIPO_COLORE_LABELS, VEHICLE_DEPOSITO_LABELS,
  VEHICLE_TIPO_CARICO_LABELS, VEHICLE_TAGLIANDO_TIPO_LABELS,
  VEHICLE_CARROZZERIA_LABELS,
  VEHICLE_TIPO_CARBURANTE_LABELS, VEHICLE_COLORE_LABELS,
  VEHICLE_COLORE_INTERNI_LABELS, VEHICLE_MATERIALE_INTERNI_LABELS,
  VEHICLE_CLASSE_AMBIENTALE_LABELS, VEHICLE_COLORE_HEX,
} from "@/lib/labels";
import NumberInput from "@/components/NumberInput";
import ClearableSelect from "@/components/ClearableSelect";
import DisponibilitaBadge from "@/components/DisponibilitaBadge";
import VehicleStatusBadge from "@/components/VehicleStatusBadge";
import DepositoPicker from "@/components/DepositoPicker";
import TipoCaricoBadge from "@/components/TipoCaricoBadge";
import CarPlaceholderSvg from "@/components/CarPlaceholderSvg";
import AvvisoPagina from "@/components/AvvisoPagina";
import CambioDisponibilitaModal from "@/components/CambioDisponibilitaModal";
import AnagraficaPicker from "@/components/AnagraficaPicker";
import NazionePicker from "@/components/NazionePicker";
import VenditaPicker from "@/components/VenditaPicker";
import DocumentsTab from "@/components/DocumentsTab";
import VehicleFotoTab from "@/components/VehicleFotoTab";
import VehicleWebTab from "@/components/VehicleWebTab";
import VenditaDetailContent, { EmptyVendita } from "@/pages/VenditaDetail";
import PreventiviTab from "@/components/PreventiviTab";
import { useDirtyGuard } from "@/hooks/useDirtyGuard";
import { AuditRowAuto } from "@/components/AuditRows";
import CurrencyInput, { CurrencyDisplay, formatCurrencyIT } from "@/components/CurrencyInput";
import SpeseVeicoloTable from "@/components/SpeseVeicoloTable";
import CodiceIvaPicker, { useCodiciIva } from "@/components/CodiceIvaPicker";
import { useConfirm } from "@/components/ConfirmProvider";
import { proponiAcquisizioni } from "@/pages/crm/CollegamentiOpportunita";
import LeadVeicolo, { useLeadVeicolo } from "@/pages/crm/LeadVeicolo";
import StampaMandatoDialog from "@/components/StampaMandatoDialog";
import { RiallineamentoCaricoDialog } from "@/components/vendite/RiallineamentoRegime";
import FlagCheck from "@/components/FlagCheck";
import Conflitti, { campiModificati, sovrascrittura } from "@/components/Conflitti";

const SPESA_STATO_LABELS = { da_fare: "Da fare", eseguito: "Eseguito", pagato: "Pagato" };

// Sprint 4I1.B2.B — visibilità campo Codice IVA per tipo_carico.
// conto_terzi (privato) NON ha IVA sul veicolo (commissioni fatturate altrove).
const TIPO_CARICO_SHOW_IVA = new Set([
  "conto_proprio_iva", "conto_proprio_art36", "conto_terzi_iva",
]);

// Campi sensibili: visibili solo a chi ha gestione_incassi
const SENSITIVE_FIELDS = new Set([
  "prezzo_acquisto", "acquisto_iva_imponibile", "acquisto_imposta",
  "imponibile_pattuito", "prezzo_pattuito",
  "miniatto", "approntamento", "margine_minimo",
  "totale_costo_auto", "prezzo_vendita_minimo",
  "delta_prezzo_su_acquisto", "recupero_su_promo",
]);
// Sprint SICUREZZA-AMBITO — regola unica in lib/ambito.js
const userHasGestioneIncassi = (user) => haFlag(user, "gestione_incassi");
const userHasAgireVenduta = (user, companyId, siteId) =>
  !!companyId && haFlag(user, "agire_su_pratica_venduta", companyId, siteId);
const userHasAgireConsegnata = (user, companyId, siteId) =>
  !!companyId && haFlag(user, "agire_su_pratica_consegnata", companyId, siteId);

const TABS = [
  { key: "dati", label: "Dati Veicolo" },
  { key: "accessori", label: "Accessori e Dotazioni", icon: Package },
  { key: "importi", label: "Carico e Importi", icon: Wallet },
  { key: "foto", label: "Foto", icon: Camera },
  { key: "web", label: "WEB", icon: Globe },
  { key: "documenti", label: "Documenti", icon: FileText },
  { key: "preventivi", label: "Preventivi", icon: FileText },
  { key: "vendita", label: "Vendita" },
  { key: "margini", label: "Margini", icon: Calculator, placeholder: "Disponibile da Sprint 4" },
  { key: "storico", label: "Storico", icon: History },
];
// Sprint CRM-6 — «Lead» a destra di Storico, solo se l'utente vede almeno una lead del veicolo (CRM-7: senza numero)
export const schedeVeicolo = (lead) => [...TABS, ...(lead?.length ? [{ key: "lead", label: "Lead" }] : [])];

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtEur = (v) => v == null || v === "" ? "—"
  : new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(v);

const emptyForm = () => ({
  stato: "usato",
  status: "VIRTUAL",
  marca: "",
  modello: "",
  data_carico: todayISO(),
  targa: "",
  telaio: "",
  prima_immatricolazione: "",
  provenienza: "",
  nazione_provenienza: "",
  seconda_immatricolazione: "",
  n_proprietari: "",
  km: "",
  versione: "",
  allestimento: "",
  cilindrata: "",
  kw: "",
  aliquota_iva_acquisto: null,
  // Sprint 4I1.B2.B — codice IVA carico veicolo (default propagato dal backend)
  iva_codice_carico: null,
  carburante: "",
  cambio: "",
  n_marce: "",
  diametro_cerchi: "",
  marca_modello_pneumatici: "",
  trazione: "",
  colore: "",
  secondo_colore: "",
  tipo_colore: "",
  deposito: null,
  n_chiave: "",
  // Sprint 2B.2-A: nuovi campi Carico veicolo
  // Sprint 2B.2-A.fix: cointestatario è ora FK a suppliers (categoria fornitore_veicoli)
  cointestatario_id: "",
  data_atto: "",
  inizio_mandato: "",
  fine_mandato: "",
  // Legacy (rimossi dall'UI ma il payload preparePayload li scarta)
  data_acquisizione: "",
  scadenza_mandato: "",
  tipo_carico: "",
  categoria_veicolo: "autoveicoli",
  supplier_id: "",
  scadenza_tassa_proprieta: "",
  ultima_revisione_data: "",
  ultima_revisione_km: "",
  prossima_revisione_data: "",
  ultimo_tagliando_data: "",
  ultimo_tagliando_tipo: "",
  ultimo_tagliando_km: "",
  prossimo_tagliando: "",
  garanzia_casa_madre_scadenza: "",
  cinghia_distribuzione_data: "",
  cinghia_distribuzione_km: "",
  bombole_ultima_revisione: "",
  note: "",
  // Sprint STAMPE (§92) — blocchi stampa (oggetti o null)
  dotazioni_extra: null,
  dati_ritiro: null,
  compenso_intermediazione: null,
  mandato_dettagli: null,
  dati_acquisto: null,
  importi: {
    listino: "", sconto_perc_su_web: "",
    acquisto_iva_imponibile: "", acquisto_imposta: "",
    prezzo_acquisto: "", imponibile_pattuito: "", prezzo_pattuito: "",
    miniatto: "", approntamento: "",
    spese_generiche: [], spese_ripristino: [],
    margine_minimo: "", prezzo_web: "", note_importi: "",
  },
  company_id: "",
  site_id: "",
  // Sprint 4H-DEP.B — FK Deposito
  deposito_id: null,
  // Sprint PERMUTA.1 — Legame inverso permuta↔vendita
  vendita_origine_id: null,
  data_previsto_arrivo: "",
  // Sprint WEB.1 — Sezione WEB (pubblicazione)
  link_youtube: "",
  link_360: "",
  web_badges: {},
  mostra_listino: false,
  testo_sito: "",
  testo_portali: "",
  iva_esposta_web: false,
  prezzo_comprensivo_iva_web: false,
});

// Sprint SCHEDA-VEICOLO — tutti i campi numerici del veicolo (stesso elenco del modello dati)
const CAMPI_NUMERICI = [
  "n_proprietari", "km", "aliquota_iva_acquisto", "cilindrata", "kw", "n_marce", "n_cilindri", "cv_fiscali",
  "coppia_max", "giri_coppia_max", "giri_potenza_max", "mot_termico_cv", "mot_termico_kw",
  "mot_elettrico_cv", "mot_elettrico_kw", "cavalli_tot_cv", "consumo_urbano", "consumo_extraurbano",
  "consumo_combinato", "consumo_combinato_gpl", "co2", "lunghezza", "larghezza", "altezza", "n_porte",
  "posti_anteriori", "posti_totali", "massa_vuoto", "massa_complessiva", "capacita_bagagliaio",
  "serbatoio_litri", "diametro_cerchi", "ultima_revisione_km", "ultimo_tagliando_km", "cinghia_distribuzione_km",
];

// Strip empty strings → null for optional fields, keep numbers as numbers
export function preparePayload(form) {
  const out = { ...form };
  // empty strings to null on enums and dates
  ["targa","telaio","prima_immatricolazione","provenienza","nazione_provenienza",
   "seconda_immatricolazione","versione","allestimento","carburante","cambio",
   "trazione","colore","secondo_colore","tipo_colore","n_chiave",
   "tipo_carico","categoria_veicolo","supplier_id","cointestatario_id",
   "data_atto","inizio_mandato","fine_mandato",
   "scadenza_tassa_proprieta","ultima_revisione_data","prossima_revisione_data",
   "ultimo_tagliando_data","ultimo_tagliando_tipo","prossimo_tagliando",
   "garanzia_casa_madre_scadenza","cinghia_distribuzione_data",
   "bombole_ultima_revisione","note","company_id","site_id","deposito_id",
   "vendita_origine_id","data_previsto_arrivo",
   "dotazioni_extra","dati_ritiro","compenso_intermediazione",
   "mandato_dettagli","dati_acquisto",
  ].forEach((k) => { if (out[k] === "") out[k] = null; });
  // Sprint WEB.1 — normalizza campi web
  if (out.mostra_listino === "" || out.mostra_listino == null) out.mostra_listino = false;
  else out.mostra_listino = !!out.mostra_listino;
  out.iva_esposta_web = !!out.iva_esposta_web;
  out.prezzo_comprensivo_iva_web = !!out.prezzo_comprensivo_iva_web;
  if (!out.web_badges || typeof out.web_badges !== "object" || Array.isArray(out.web_badges)) out.web_badges = {};
  // Regola unica: un campo numerico lasciato vuoto si manda come «nessun valore», mai come "".
  CAMPI_NUMERICI.forEach((k) => {
    if (!(k in out)) return;
    out[k] = out[k] === "" || out[k] == null ? null : Number(out[k]);
  });
  // Sprint 2B.2-A: deposito è ora object (o null)
  if (
    out.deposito === "" ||
    out.deposito === undefined ||
    typeof out.deposito !== "object" ||
    out.deposito === null
  ) {
    out.deposito = null;
  }
  // Sprint 2B.2-A.fix: cointestatario_id è FK string (o null)
  if (!out.cointestatario_id || typeof out.cointestatario_id !== "string") {
    out.cointestatario_id = null;
  }
  // Rimuovi sempre il campo legacy object (se esistesse nel form)
  delete out.cointestatario;
  // Rimuovi campi legacy (deprecati Sprint 2B.2-A) prima di inviare
  delete out.data_acquisizione;
  delete out.scadenza_mandato;
  // importi: numerics
  if (out.importi) {
    const i = { ...out.importi };
    ["listino","sconto_perc_su_web","acquisto_iva_imponibile","acquisto_imposta",
     "prezzo_acquisto","imponibile_pattuito","prezzo_pattuito",
     "margine_minimo","prezzo_web","prezzo_b2b"
    ].forEach((k) => {
      if (i[k] === "" || i[k] == null) i[k] = null;
      else i[k] = Number(i[k]);
    });
    if (i.note_importi === "") i.note_importi = null;
    // Sprint 4I1.B1.1 (BUG 3 fix): NON inviare alias derived dal client.
    // La fonte di verità è `importi.spese_veicolo[]`. Gli alias derived
    // (miniatto, approntamento, spese_generiche/ripristino, totali) vengono
    // ricomputati lato server. Se li inviassimo, il backward compat backend
    // li interpreterebbe come "input legacy" e sovrascriverebbe i valori
    // veri causando accumulo (vedi diagnostica Sprint 4I1.B1.1).
    [
      "miniatto", "approntamento", "trasporto",
      "spese_generiche", "spese_ripristino",
      "totale_spese_veicolo", "totale_spese_generiche_derived",
      "totale_spese_ripristino_derived",
      "totale_da_fare", "totale_eseguito", "totale_pagato",
      "totale_costo_auto", "prezzo_vendita_minimo",
      "delta_prezzo_su_acquisto", "recupero_su_promo",
      "sconto_perc_prezzo_web", "sconto_eur_prezzo_web_da_listino",
    ].forEach((k) => delete i[k]);
    // Le righe spese_veicolo NON vengono spedite via PATCH /vehicles, ma
    // gestite tramite gli endpoint dedicati /vehicles/{id}/spese_veicolo.
    delete i.spese_veicolo;
    out.importi = i;
  }
  return out;
}

// Sprint 4H-DEP.B — Helper: il deposito è FISICO di una sede diversa dalla nuova?
// True → al cambio sede deve essere resettato. False → resta (SISTEMA o stessa sede).
function _isFisicoOfDifferentSite(cache, depositoId, newSiteId) {
  if (!depositoId || !newSiteId) return false;
  const d = (cache || []).find((x) => x.id === depositoId);
  if (!d) return false; // non conosciamo l'origine: meglio non resettare
  if (d.tipo === "SISTEMA") return false;
  return d.sede_id !== newSiteId;
}



export default function VehicleDetail() {
  const [riallineamento, setRiallineamento] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const elev = isElevated(user);
  const confirm = useConfirm();
  const isNew = !id;

  const [tab, setTab] = useState("dati");
  const [searchParams, setSearchParams] = useSearchParams();
  // Sync ?tab= con stato locale
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && t !== tab) setTab(t);
    // eslint-disable-next-line
  }, [searchParams]);
  // Vendita associata (Sprint 4A.1)
  const [associatedVendita, setAssociatedVendita] = useState(null);
  // Navigazione condivisa: chiude la stampa, apre la scheda e scorre alla card indicata.
  // Sprint SCHEDA-VEICOLO — con `campo` si arriva al campo (data-campo), lo si evidenzia e ci si mette il cursore.
  const goTo = (t, target, campo) => {
    setOpenStampa(false);
    setTab(t);
    const sel = campo ? `[data-campo="${campo}"]` : target ? `[data-testid="${target}"]` : null;
    if (!sel) return;
    setTimeout(() => {
      const el = document.querySelector(sel);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (!campo) return;
      el.querySelector("input, button, [role='combobox']")?.focus({ preventScroll: true });
      const evid = ["ring-2", "ring-amber-400", "ring-offset-2", "rounded-md"];
      el.classList.add(...evid);
      setTimeout(() => el.classList.remove(...evid), 2500);
    }, 450);
  };
  const [form, setForm] = useState(emptyForm());
  // Sprint 4I1.B2.B — lista codici IVA (cached) per lookup aliquota
  const codiciIva = useCodiciIva();
  // Sprint 4X.4 E1: snapshot caricato dal server, usato per derivare dirty in modo affidabile
  const [initialFormSnapshot, setInitialFormSnapshot] = useState(null);
  // Sprint CONCORRENZA — esito del salvataggio con campi cambiati da altri
  const [esitoConflitti, setEsitoConflitti] = useState(null);
  const [mieiValori, setMieiValori] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  // Sprint CRM-6 — lead CRM del veicolo visibili all'utente: la tab «Lead N» c'è solo se ce n'è almeno una
  const lead = useLeadVeicolo(id, tab);
  // Sprint SCHEDA-VEICOLO — precedente/successivo per codice (stessa azienda, ambito utente, senza cestino)
  const [adiacenti, setAdiacenti] = useState(null);
  useEffect(() => {
    setAdiacenti(null);
    if (!id) return;
    api.get(`/vehicles/${id}/adiacenti`).then((r) => setAdiacenti(r.data)).catch(() => setAdiacenti(null));
  }, [id]);
  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);
  // Sprint 4H-DEP.B — cache progressiva dei depositi visti, usata per la logica
  // di reset al cambio sede (sapere se il deposito attuale è FISICO/SISTEMA).
  const [depositoCache, setDepositoCache] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [saving, setSaving] = useState(false);
  const [openCambio, setOpenCambio] = useState(false);
  const [openStampa, setOpenStampa] = useState(false);
  // Sprint 4A.6: dirty guard per avviso "salva prima di uscire"
  const [isDirty, setIsDirty] = useState(false);
  // Sprint 4A.5 C1+C3: sblocco temporaneo per veicoli CONSEGNATI
  const [tempUnlocked, setTempUnlocked] = useState(false);

  // Sprint 4A.5 C2 + C1+C3: permessi di azione sul veicolo in stato avanzato
  const canActVenduta = useMemo(
    () => userHasAgireVenduta(user, vehicle?.company_id, vehicle?.site_id),
    [user, vehicle?.company_id, vehicle?.site_id]
  );
  const canActConsegnata = useMemo(
    () => userHasAgireConsegnata(user, vehicle?.company_id, vehicle?.site_id),
    [user, vehicle?.company_id, vehicle?.site_id]
  );
  // Lock derivato: SOLD → bloccato se non posso agire venduta; DELIVERED → bloccato
  // se non posso agire consegnata, O se posso ma non ho ancora sbloccato temporaneamente.
  const vehicleLocked = useMemo(() => {
    if (!vehicle) return false;
    if (vehicle.status === "SOLD") return !canActVenduta;
    if (vehicle.status === "DELIVERED") return !(canActConsegnata && tempUnlocked);
    return false;
  }, [vehicle, canActVenduta, canActConsegnata, tempUnlocked]);

  // Load options
  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data || [])).catch(() => {});
    // Sprint 2D-PART-B: usa /api/anagrafiche al posto di /api/suppliers (legacy)
    api.get("/anagrafiche?page_size=200").then((r) => setSuppliers(r.data?.items || [])).catch(() => {});
  }, []);

  // Load sites when company changes
  useEffect(() => {
    if (!form.company_id) { setSites([]); return; }
    api.get(`/companies/${form.company_id}/sites`).then((r) => setSites(r.data || [])).catch(() => {});
  }, [form.company_id]);

  // Sprint 2B.2-A: se il company_id cambia rispetto al veicolo caricato e il
  // deposito attuale è "sede" di un'altra company, resetta a null con avviso.
  const prevCompanyRef = useRef(null);
  useEffect(() => {
    const prev = prevCompanyRef.current;
    if (prev !== null && prev !== form.company_id) {
      const dep = form.deposito;
      if (dep && typeof dep === "object" && dep.tipo === "sede") {
        set({ deposito: null });
        toast.warning("Sede di deposito resettata. Seleziona una sede della nuova azienda.");
      }
    }
    prevCompanyRef.current = form.company_id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.company_id]);

  // Load vehicle if editing
  const reloadVehicle = React.useCallback((vid) => {
    const target = vid || id;
    if (!target) return;
    api.get(`/vehicles/${target}`).then((r) => {
      const v = r.data;
      setVehicle(v);
      const base = emptyForm();
      const coerce = (obj) => {
        const out = {};
        for (const [k, val] of Object.entries(obj)) {
          const baseVal = base[k];
          const isObjectField = baseVal !== null
            && typeof baseVal === "object"
            && !Array.isArray(baseVal);
          const isNullableObjectField = baseVal === null && (
            val === null || (typeof val === "object" && !Array.isArray(val))
          );
          if (isObjectField || isNullableObjectField) {
            out[k] = val;
            continue;
          }
          if (val === null || val === undefined) out[k] = "";
          else if (typeof val === "object" && !Array.isArray(val)) out[k] = val;
          else out[k] = val;
        }
        return out;
      };
      const merged = { ...base, ...v };
      const cleaned = coerce(merged);
      const importiBase = { ...emptyForm().importi, ...(v.importi || {}) };
      const importiClean = {};
      for (const [k, val] of Object.entries(importiBase)) {
        if (Array.isArray(val)) importiClean[k] = val;
        else if (val === null || val === undefined) importiClean[k] = "";
        else importiClean[k] = val;
      }
      setForm({ ...cleaned, importi: importiClean });
      setInitialFormSnapshot({ ...cleaned, importi: importiClean });
      setIsDirty(false);
    }).catch(() => {
      toast.error("Veicolo non trovato");
      navigate("/vehicles");
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  useEffect(() => {
    if (!id) return;
    // Carica anche la vendita associata (Sprint 4A.1)
    api.get(`/vendite?vehicle_id=${id}&tipo=pratica&exclude_status=annullata&page_size=10`).then((r) => {
      const arr = (r.data?.items || []);
      setAssociatedVendita(arr[0] || null);
    }).catch(() => setAssociatedVendita(null));
    reloadVehicle(id);
  }, [id, reloadVehicle, searchParams]);
  useEffect(() => {
    if (!id) return;
    api.get(`/audit-log?entity_type=vehicle&entity_id=${id}`).then((r) => {
      setAudit(r.data?.items || r.data || []);
    }).catch(() => {});
  }, [id, tab]);

  // Sprint 4X.4 E1: dirty derivato da confronto deep tra snapshot e form
  // così se l'utente apre la scheda senza toccare nulla → dirty resta false
  // anche se gli onChange handler sono triggerati internamente dal populate.
  useEffect(() => {
    if (!initialFormSnapshot) return;
    const same = JSON.stringify(form) === JSON.stringify(initialFormSnapshot);
    setIsDirty(!same);
  }, [form, initialFormSnapshot]);

  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); };
  const setI = (patch) => { setForm((f) => ({ ...f, importi: { ...f.importi, ...patch } })); };

  // Live calculated values
  const calcImporti = useMemo(() => {
    const i = form.importi || {};
    const n = (v) => (v === "" || v == null ? 0 : Number(v) || 0);
    const sumSpese = (arr) => (arr || []).reduce((s, x) => s + n(x.importo), 0);
    const totale = n(i.prezzo_acquisto) + n(i.miniatto) + n(i.approntamento)
      + sumSpese(i.spese_generiche) + sumSpese(i.spese_ripristino);
    const prezzoVendMin = totale + n(i.margine_minimo);
    const delta = n(i.prezzo_web) - totale;
    const recupero = prezzoVendMin - n(i.prezzo_web);
    // Sprint 4I1.B2.B — IVA invertita (preview client-side).
    // L'aliquota proviene dal codice IVA carico (lookup via cache CodiciIva).
    // Sprint SCHEDA-VEICOLO — nessun valore predefinito: finché l'aliquota non è nota non si mostra niente.
    const isIva = TIPO_CARICO_SHOW_IVA.has(form.tipo_carico);
    const ivaRow = form.iva_codice_carico
      ? (codiciIva || []).find((c) => c.codice === form.iva_codice_carico) : null;
    const aliq = ivaRow ? Number(ivaRow.aliquota) || 0 : null;
    const pa = n(i.prezzo_acquisto);
    const imponibile = isIva && pa > 0 && aliq !== null
      ? Math.round((pa / (1 + aliq / 100)) * 100) / 100
      : null;
    const imposta = isIva && pa > 0 && imponibile !== null
      ? Math.round((pa - imponibile) * 100) / 100
      : null;
    // Sprint 4C (L1): sconto % e sconto € da listino casa madre.
    // Visibili solo se listino > 0 (specifica L1). Formattazione italiana.
    const lis = n(i.listino);
    const pweb = n(i.prezzo_web);
    const sconto = lis > 0 ? Math.round(((lis - pweb) / lis) * 1000) / 10 : null;
    const scontoEur = lis > 0 ? Math.round((lis - pweb) * 100) / 100 : null;
    return { totale, prezzoVendMin, delta, recupero, imponibile, imposta, sconto, scontoEur };
  }, [form.importi, form.tipo_carico, form.iva_codice_carico, codiciIva]);

  const liveCv = useMemo(() => {
    const k = Number(form.kw);
    return form.kw && !isNaN(k) ? Math.round((k / 0.7457) * 10) / 10 : null;
  }, [form.kw]);

  // Sprint 4G.B6 — popup disponibilità fisica al PRIMO salvataggio
  const [dispPopupOpen, setDispPopupOpen] = useState(false);
  const [dispChoice, setDispChoice] = useState("AVAILABLE");
  const [pendingNewVehicle, setPendingNewVehicle] = useState(null);

  const computeDefaultDispFisica = (v) => {
    // Stesse regole di compute_default_disponibilita_fisica backend.
    if (v.vendita_origine_id) return "INCOMING";
    const flagImm = !!v.flag_da_immatricolare;
    if (v.targa && !flagImm) return "AVAILABLE";
    if (flagImm) {
      const st = (v.stato || "").toLowerCase();
      if (st === "nuova") return "VIRTUAL";
      if (st === "km0" || st === "usato") return "AVAILABLE";
    }
    return "AVAILABLE";
  };

  const onSave = async () => {
    // Sprint 4G.B6 — validazione client-side dei campi obbligatori (UX).
    // Backend ha la difesa con strict_required=true.
    const missing = [];
    const fl = form;
    const fi = fl.importi || {};
    if (!fl.company_id) missing.push("Azienda");
    if (!fl.site_id) missing.push("Sede");
    if (!fl.deposito_id) missing.push("Deposito");
    if (!fl.categoria_veicolo) missing.push("Tipo veicolo");
    if (!fl.stato) missing.push("Condizioni");
    if (fl.km === null || fl.km === undefined || fl.km === "") missing.push("Km");
    if (!fl.marca) missing.push("Marca");
    if (!fl.modello) missing.push("Modello");
    if (!fl.provenienza) missing.push("Provenienza");
    if (fl.provenienza === "importazione" && !fl.nazione_provenienza) missing.push("Nazione provenienza");
    if (!fl.tipo_carico) missing.push("Tipo di carico");
    if (!fl.supplier_id) missing.push("Fornitore");
    if (fi.prezzo_acquisto === null || fi.prezzo_acquisto === undefined || fi.prezzo_acquisto === "")
      missing.push("Prezzo acquisto");
    if (fi.margine_minimo === null || fi.margine_minimo === undefined || fi.margine_minimo === "")
      missing.push("Margine minimo");
    if (!fl.flag_da_immatricolare) {
      if (!fl.targa) missing.push("Targa (o flag 'Da immatricolare')");
      if (!fl.prima_immatricolazione) missing.push("Prima immatricolazione (o flag)");
      const isCP = (fl.tipo_carico || "").startsWith("conto_proprio");
      const isCT = (fl.tipo_carico || "").startsWith("conto_terzi");
      if (isCP && !fl.data_atto) missing.push("Data atto (o flag)");
      if (isCT && !fl.inizio_mandato) missing.push("Inizio mandato (o flag)");
    }
    if ((fl.stato || "").toLowerCase() !== "nuova") {
      if (fl.n_proprietari === null || fl.n_proprietari === undefined || fl.n_proprietari === "")
        missing.push("N° proprietari (o 'Nuova')");
    }
    if (missing.length > 0) {
      toast.error("Campi obbligatori mancanti: " + missing.join(", "));
      return;
    }
    // B3: confirm if changing company/site on existing vehicle
    if (vehicle && (vehicle.company_id !== form.company_id || vehicle.site_id !== form.site_id)) {
      const oldComp = companies.find((c) => c.id === vehicle.company_id);
      const newComp = companies.find((c) => c.id === form.company_id);
      const oldSite = sites.find((s) => s.id === vehicle.site_id);
      const newSite = sites.find((s) => s.id === form.site_id);
      const ok = await confirm({
        title: "Conferma cambio sede/azienda",
        message: `Stai spostando il veicolo da ${oldComp?.nome_breve || oldComp?.name || "—"} – ${oldSite?.nome || "—"} a ${newComp?.nome_breve || newComp?.name || "—"} – ${newSite?.nome || "—"}.\nQuesta modifica può influire su visibilità e responsabilità del veicolo.\nProcedere?`,
        confirmLabel: "Conferma cambio",
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      const payload = preparePayload(form);
      if (isNew) {
        // POST con strict_required=true per attivare validazione backend 4G.B6
        const r = await api.post("/vehicles?strict_required=true", payload);
        toast.success(`Veicolo creato: ${r.data.codice}`);
        proponiAcquisizioni(confirm, r.data.crm_proposte, r.data.id);  // Sprint CRM-3
        // Sprint 4G.B6 — popup primo salvataggio con preselezione disponibilità
        const preselect = computeDefaultDispFisica(r.data);
        setDispChoice(preselect);
        setPendingNewVehicle(r.data);
        setDispPopupOpen(true);
      } else {
        // Sprint CONCORRENZA — solo i campi cambiati, ciascuno con il valore che aveva al caricamento
        const { payload: diff, originali } = campiModificati(preparePayload(initialFormSnapshot), payload);
        const r = await api.patch(`/vehicles/${id}?strict_required=true`, { ...diff, originali });
        setVehicle(r.data);
        if (r.data.conflitti?.length) { setMieiValori(diff); setEsitoConflitti(r.data); }
        else toast.success("Veicolo salvato");
        const ra = r.data.riallineamento_regime;
        if (ra && (ra.preventivi || ra.pratiche || (ra.non_riallineati || []).length)) setRiallineamento(ra);
        // Sprint 4A.5 C3: dopo un Salva riuscito, chiudi lo sblocco temporaneo
        if (tempUnlocked) setTempUnlocked(false);
        // Sprint CONCORRENZA: scheda e punto di partenza ricaricati dal server (anche le modifiche altrui)
        reloadVehicle(id);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === "string" ? detail : null) || "Errore salvataggio";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const applicaConflitti = async (scelte) => {
    const miei = esitoConflitti.conflitti.filter((c) => scelte[c.campo] === "mio");
    setEsitoConflitti(null);
    if (!miei.length) { toast.success("Tenuti i valori attuali"); return; }
    const { payload, originali } = sovrascrittura(miei, mieiValori);
    try {
      const r = await api.patch(`/vehicles/${id}?strict_required=true`, { ...payload, originali });
      setVehicle(r.data);
      if (r.data.conflitti?.length) { setEsitoConflitti(r.data); }
      else toast.success(`Sovrascritt${miei.length === 1 ? "o 1 campo" : `i ${miei.length} campi`}`);
      reloadVehicle(id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    }
  };

  // Sprint 4G.B6 — conferma scelta disponibilità fisica al primo salvataggio.
  // Il veicolo nasce VIRTUAL: per portarlo a INCOMING/AVAILABLE si usa l'endpoint
  // di transizione (workflow rigido 2B.2-B), NON una PATCH diretta dello status.
  // Se la scelta coincide con lo stato di nascita, nessuna transizione (no-op).
  const confirmDispFisicaFirstSave = async () => {
    if (!pendingNewVehicle) { setDispPopupOpen(false); return; }
    const current = pendingNewVehicle.status || "VIRTUAL";
    try {
      if (dispChoice !== current) {
        await api.post(`/vehicles/${pendingNewVehicle.id}/transition`, {
          target_status: dispChoice,
        });
      }
      toast.success(`Veicolo creato con disponibilità ${dispChoice}`);
      setDispPopupOpen(false);
      const newId = pendingNewVehicle.id;
      setPendingNewVehicle(null);
      navigate(`/vehicles/${newId}`);
    } catch (e) {
      toast.error("Errore aggiornamento disponibilità: " + (e?.response?.data?.detail || e.message));
    }
  };

  // Sprint 4A.6: dirty guard modal (navigazione tra tab interni non triggera)
  const { GuardModal, requestNavigate } = useDirtyGuard({
    isDirty,
    onSave,
    baseUrl: id ? `/vehicles/${id}` : undefined,
  });

  const onDelete = async () => {
    const ok = await confirm({
      title: "Elimina veicolo",
      message: `Vuoi eliminare ${vehicle?.codice}?\nIl veicolo andrà nel cestino.`,
      confirmLabel: "SI, elimina",
    });
    if (!ok) return;
    const motivo = window.prompt("Inserisci il motivo dell'eliminazione (min 5 caratteri):", "");
    if (!motivo || motivo.trim().length < 5) {
      toast.error("Motivo richiesto (min 5 caratteri)");
      return;
    }
    try {
      await api.delete(`/vehicles/${id}`, { data: { motivo: motivo.trim() } });
      toast.success("Veicolo spostato nel cestino");
      navigate("/vehicles");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === "string" ? detail : null) || "Errore eliminazione";
      toast.error(msg);
    }
  };

  const onDuplica = async () => {
    try {
      const r = await api.post(`/vehicles/${id}/duplica`);
      toast.success(`Duplicato: ${r.data.codice}`);
      await proponiAcquisizioni(confirm, r.data.crm_proposte, r.data.id);  // Sprint CRM-3
      navigate(`/vehicles/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore duplica");
    }
  };

  const updateSpesa = (key, idx, patch) => {
    setI({ [key]: form.importi[key].map((x, i) => i === idx ? { ...x, ...patch } : x) });
  };
  const removeSpesa = (key, idx) => {
    setI({ [key]: form.importi[key].filter((_, i) => i !== idx) });
  };
  const addSpesa = (key) => {
    setI({ [key]: [...(form.importi[key] || []), { descrizione: "", importo: "", supplier_id: null, data: null, note: "", stato: "da_fare" }] });
  };
  // Sprint 4C (L3): sposta una voce tra i 2 array (cambio categoria)
  const moveSpesa = (fromKey, toKey, idx) => {
    const src = form.importi[fromKey] || [];
    const dst = form.importi[toKey] || [];
    const item = src[idx];
    if (!item) return;
    setI({
      [fromKey]: src.filter((_, i) => i !== idx),
      [toKey]: [...dst, item],
    });
  };

  const cardOptions = (entries) => Object.entries(entries).map(([k, v]) => (
    <SelectItem key={k} value={k}>{v}</SelectItem>
  ));

  const currentTab = TABS.find((t) => t.key === tab);

  // Sprint UI-HEADER — Copertina veicolo (foto[0] categoria VEICOLO)
  const copertina = useMemo(() => {
    const arr = (vehicle?.foto || []).filter((f) => (f.categoria || "VEICOLO") === "VEICOLO");
    arr.sort((a, b) => (a.ord || 0) - (b.ord || 0));
    return arr[0] || null;
  }, [vehicle?.foto]);

  return (
    <div className="space-y-4" data-testid="vehicle-detail-page">
      {/* Header — Sprint SCHEDA-VEICOLO: sinistra lista+frecce | foto | 3 righe dati · destra 2 righe comandi */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch lg:justify-between">
        <div className="flex items-stretch gap-3 min-w-0">
          <div className="flex flex-col justify-center gap-2 shrink-0" data-testid="vehicle-header-nav">
              <Button
                variant="outline"
                size="sm"
                onClick={() => requestNavigate("/vehicles")}
                className="h-9 border-blue-500 text-blue-700 hover:bg-blue-50 hover:text-blue-800 hover:border-blue-600"
                data-testid="vehicle-back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Lista Veicoli
              </Button>
            {!isNew && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => adiacenti?.precedente && requestNavigate(`/vehicles/${adiacenti.precedente.id}`)}
                  disabled={!adiacenti?.precedente}
                  className="px-2.5"
                  title={adiacenti?.precedente ? `Veicolo precedente: ${adiacenti.precedente.codice}` : "Primo veicolo dell'azienda"}
                  aria-label="Veicolo precedente"
                  data-testid="vehicle-prev-btn"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => adiacenti?.successivo && requestNavigate(`/vehicles/${adiacenti.successivo.id}`)}
                  disabled={!adiacenti?.successivo}
                  className="px-2.5"
                  title={adiacenti?.successivo ? `Veicolo successivo: ${adiacenti.successivo.codice}` : "Ultimo veicolo dell'azienda"}
                  aria-label="Veicolo successivo"
                  data-testid="vehicle-next-btn"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Miniatura copertina — altezza ~ header (più grande) */}
          <div
            className="shrink-0 rounded-md overflow-hidden border border-slate-200 bg-slate-100 self-stretch"
            style={{ width: "140px", minHeight: "100px" }}
            data-testid="vehicle-cover-thumb"
            aria-label="Copertina veicolo"
          >
            {copertina ? (
              <img
                src={vehicleFotoUrl(vehicle.id, copertina.id)}
                alt=""
                className="w-full h-full object-cover select-none pointer-events-none"
                draggable={false}
                loading="lazy"
                data-testid="vehicle-cover-img"
              />
            ) : (
              <CarPlaceholderSvg className="w-full h-full pointer-events-none" />
            )}
          </div>

          <div className="min-w-0 flex flex-col justify-center gap-1">
            <div className="flex items-center" data-testid="vehicle-header-row1">
              <TargaVeicolo targa={form.targa} />
            </div>
            <div className="flex items-center gap-2" data-testid="vehicle-header-row2">
              {form.tipo_carico && <TipoCaricoBadge value={form.tipo_carico} />}
              <span className="font-mono text-blue-700 text-sm" data-testid="vehicle-codice">
                {vehicle?.codice || "Nuovo veicolo"}
              </span>
            </div>
            <h1 className="font-heading text-xl sm:text-2xl font-semibold text-slate-900 truncate" data-testid="vehicle-header-row3">
              {form.marca || "..."} {form.modello}
              {form.versione && (
                <span className="text-slate-500 text-base font-normal"> {form.versione}</span>
              )}
            </h1>
          </div>
        </div>

        <div className="flex flex-col justify-center gap-2 lg:items-end shrink-0" data-testid="vehicle-header-azioni">
          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          {!isNew && (
            <Button
              onClick={() => setOpenStampa(true)}
              className="bg-violet-900 hover:bg-violet-950 text-white"
              title="Stampa documenti del veicolo"
              data-testid="stampa-mandato-btn"
            >
              <Printer className="h-4 w-4 mr-1" /> Stampa
            </Button>
          )}
          {!isNew && (
            <Button
              variant="outline"
              onClick={onDuplica}
              title="Duplica VEICOLO"
              aria-label="Duplica VEICOLO"
              className="px-2.5"
              data-testid="duplica-btn"
            >
              <Copy className="h-4 w-4" />
            </Button>
          )}
          {!isNew && vehicle?.can_delete !== false && (
            <Button
              variant="outline"
              onClick={onDelete}
              className="px-2.5 text-red-600 hover:bg-red-50"
              title="Elimina VEICOLO"
              aria-label="Elimina VEICOLO"
              data-testid="elimina-btn"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          {!isNew && vehicle?.can_delete === false && (
            <Button
              variant="outline" disabled
              className="px-2.5 text-slate-300 cursor-not-allowed"
              title="Eliminazione non consentita: il veicolo ha una pratica impegnata/venduta/consegnata collegata"
              aria-label="Elimina VEICOLO (disabilitato)"
              data-testid="elimina-btn-disabled"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button onClick={onSave} disabled={saving || vehicleLocked} className="bg-blue-600 hover:bg-blue-700" data-testid="save-btn">
            <Save className="h-4 w-4 mr-1" /> {saving ? "Salvataggio..." : "Salva"}
          </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          {vehicle?.status && (
            <VehicleStatusBadge
              disponibilita_fisica={vehicle.disponibilita_fisica}
              stato_commerciale={vehicle.stato_commerciale_corrente}
            />
          )}
          {!isNew && (
            <Button
              variant="outline"
              onClick={() => setOpenCambio(true)}
              disabled={vehicleLocked}
              className="border-blue-500 text-blue-700 hover:bg-blue-50 hover:text-blue-800 hover:border-blue-600"
              title={vehicleLocked ? (vehicle?.status === "SOLD"
                ? "Pratica venduta: richiede permesso 'Agisci su pratica venduta'"
                : "Pratica consegnata: richiede permesso 'Agisci su pratica consegnata'") : "Cambia disponibilità"}
              data-testid="cambia-disp-btn"
            >
              <RefreshCcw className="h-4 w-4 mr-1" /> Disponibilità
            </Button>
          )}
          </div>
        </div>
      </div>

      {/* Sprint UI-GLOBALE — avvisi senza comando in questo punto: centro avvisi (regola unica in AvvisoPagina) */}
      {(form.status === "VIRTUAL" || form.status === "INCOMING") && (
        <AvvisoPagina id="veicolo-non-in-sede" livello="info"
          testo="Veicolo non ancora in sede. Compila Azienda e Sede per renderlo Disponibile." />
      )}
      {vehicle?.status === "SOLD" && (
        <AvvisoPagina id="veicolo-venduto" livello={canActVenduta ? "segnalazione" : "critico"}
          testo={canActVenduta
            ? "Pratica venduta: modifiche abilitate grazie al permesso 'Agisci su pratica venduta'."
            : "Pratica venduta. Per modificare richiede permesso 'Agisci su pratica venduta'."} />
      )}
      {vehicle?.status === "DELIVERED" && (
        <AvvisoPagina id="veicolo-consegnato"
          livello={tempUnlocked ? "info" : canActConsegnata ? "segnalazione" : "critico"}
          testo={tempUnlocked
            ? "Modifiche temporaneamente attive. Al salvataggio le schede saranno ribloccate."
            : canActConsegnata
              ? "Pratica consegnata. Schede in sola lettura. Usa 'Sblocca temporaneamente' per modificare."
              : "Pratica consegnata. Schede in sola lettura. Per modificare richiede permesso 'Agisci su pratica consegnata'."}
          link={canActConsegnata && !tempUnlocked ? { label: "Vai alla scheda Vendita", onClick: () => setTab("vendita") } : null} />
      )}
      {calcImporti.recupero > 0 && (
        <AvvisoPagina id="veicolo-sotto-margine" livello="segnalazione"
          testo={`Stato sotto-margine: il prezzo web è inferiore al prezzo di vendita minimo di ${fmtEur(calcImporti.recupero)}.`}
          link={{ label: "Vai a Carico e Importi", onClick: () => setTab("importi") }} />
      )}

      {/* Sprint 2B.2-A: warnings dati di carico incompleti (solo in EDIT) */}
      {id && (vehicle?.warnings || []).map((w, idx) => (
        <CaricoWarningBanner
          key={`${w.code}-${idx}`}
          warning={w}
          onGoToField={() => {
            // Sprint 4G.WARN-DIAG fix: il target tab dipende dal campo.
            // Dopo 4G.1-A la card "Carico veicolo" vive nel tab "importi"
            // (Carico e Importi). Mapping per code/campo:
            //  - data_atto / inizio_mandato / fine_mandato → tab "importi"
            //  - default (campi anagrafici/identificativi) → tab "dati"
            const CARICO_FIELDS = new Set([
              "data_atto", "inizio_mandato", "fine_mandato",
              "supplier_id", "cointestatario_id", "tipo_carico",
              "iva_codice_carico",
            ]);
            const targetTab = CARICO_FIELDS.has(w.field) ? "importi" : "dati";
            setTab(targetTab);
            setTimeout(() => {
              const el = document.querySelector(`[data-testid="${w.field}-input"]`);
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                el.focus?.();
              } else {
                // Fallback: scroll to "Carico veicolo" section header
                const section = Array.from(document.querySelectorAll("h2, h3, .font-heading"))
                  .find((n) => /carico veicolo/i.test(n.textContent || ""));
                section?.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }, 100);
          }}
        />
      ))}

      {/* Tabs */}
      {/* Sprint UI-GLOBALE — barra schede scura, ancorata in alto durante lo scorrimento */}
      <div className="sticky top-0 z-20 -mx-2 px-2 bg-slate-900 rounded-md shadow-sm" data-testid="vehicle-tabs-bar">
        <div className="flex gap-1 overflow-x-auto" role="tablist">
          {schedeVeicolo(lead).filter((t) => !t.elevatedOnly || elev).map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-[3px] transition-colors ${
                tab === t.key
                  ? "border-sky-400 bg-slate-700 text-white font-semibold"
                  : "border-transparent text-slate-200 font-medium hover:bg-slate-800 hover:text-white"
              }`}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content — Sprint 4A.5: fieldset wrapping per disabilitare tutti gli input quando locked */}
      <fieldset disabled={vehicleLocked} className={vehicleLocked ? "opacity-75" : ""} data-testid="vehicle-content-fieldset">
      {tab === "dati" && (
        <div className="space-y-4">
          {/* Sprint 4G.B1 — CARD "Competenza e tipo" (griglia finale) */}
          <Card className="p-4" data-testid="card-competenza-tipo">
            <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
              <div className="font-heading text-sm uppercase text-slate-500 tracking-wide">Competenza e tipo</div>
              {form.data_carico && (
                <div className="text-[11px] uppercase text-slate-400 tracking-wide">
                  C: {form.data_carico}
                </div>
              )}
            </div>
            {/* RIGA 1 — 4 colonne (Chiave stretta) */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-3">
              <div className="md:col-span-3">
                <Field label="Azienda" required>
                  <Select value={form.company_id || ""} onValueChange={(v) => set({ company_id: v, site_id: "" })}>
                    <SelectTrigger data-testid="comp-azienda"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome_breve || c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="md:col-span-4">
                <Field label="Sede di competenza" required>
                  <Select value={form.site_id || ""} onValueChange={(v) => {
                    const patch = { site_id: v };
                    if (form.deposito_id && _isFisicoOfDifferentSite(depositoCache, form.deposito_id, v)) {
                      patch.deposito_id = null;
                      toast.warning("Deposito reazzerato: seleziona un deposito della nuova sede.");
                    }
                    set(patch);
                  }} disabled={!form.company_id}>
                    <SelectTrigger data-testid="comp-sede"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="md:col-span-3">
                <Field label="Deposito" required>
                  <DepositoPicker
                    value={form.deposito_id}
                    onChange={(id) => set({ deposito_id: id })}
                    siteId={form.site_id || null}
                    onLoad={(items) => setDepositoCache((prev) => {
                      const map = new Map(prev.map((d) => [d.id, d]));
                      for (const it of items) map.set(it.id, it);
                      return Array.from(map.values());
                    })}
                    testIdPrefix="vehicle-deposito-picker"
                    label=""
                  />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Chiave n°">
                  <NumberInput mode="integer"
                               value={form.n_chiave}
                               onChange={(v) => set({ n_chiave: v == null ? "" : String(v) })}
                               testId="comp-n-chiave" />
                </Field>
              </div>
            </div>
            {/* RIGA 2 — 6 colonne (flag centrati verticalmente) */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-3">
                <Field label="Tipo veicolo" required>
                  <Select value={form.categoria_veicolo || "autoveicoli"}
                          onValueChange={(v) => set({ categoria_veicolo: v })}>
                    <SelectTrigger data-testid="veh-categoria-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="autoveicoli">Autoveicoli</SelectItem>
                      <SelectItem value="motoveicoli">Motoveicoli</SelectItem>
                      <SelectItem value="veicoli_commerciali">Veicoli commerciali</SelectItem>
                      <SelectItem value="auto_storica">Auto storica</SelectItem>
                      <SelectItem value="roulotte_camper">Roulotte / Camper</SelectItem>
                      <SelectItem value="altro">Altro</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="md:col-span-2 flex flex-col justify-center gap-2 pt-5">
                <FlagCheck label="N1 Autocarro" testId="flag-n1-autocarro"
                  checked={!!form.flag_n1_autocarro}
                  onChange={(b) => set({ flag_n1_autocarro: b })} />
                <FlagCheck label="Neopatentati" testId="flag-neopatentati"
                  checked={!!form.flag_neopatentati}
                  onChange={(b) => set({ flag_neopatentati: b })} />
              </div>
              <div className="md:col-span-2">
                <Field label="Condizioni" required>
                  <Select value={form.stato} onValueChange={(v) => set({ stato: v })}>
                    <SelectTrigger data-testid="comp-stato-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{cardOptions(VEHICLE_STATO_LABELS)}</SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="md:col-span-1 flex flex-col justify-center gap-2 pt-5">
                <FlagCheck label="Da immatricolare" testId="flag-da-immatricolare"
                  checked={!!form.flag_da_immatricolare}
                  onChange={(b) => set({ flag_da_immatricolare: b })} />
                <FlagCheck label="Storico" testId="flag-storico"
                  checked={!!form.flag_storico}
                  onChange={(b) => set({ flag_storico: b })} />
              </div>
              <div className="md:col-span-1 flex flex-col justify-center gap-2 pt-5">
                <FlagCheck label="Aziendale" testId="flag-aziendale"
                  checked={!!form.flag_aziendale}
                  onChange={(b) => set({ flag_aziendale: b })} />
                <FlagCheck label="Demo" testId="flag-demo"
                  checked={!!form.flag_demo}
                  onChange={(b) => set({ flag_demo: b })} />
              </div>
              <div className="md:col-span-3">
                <Field label="Carrozzeria">
                  <ClearableSelect
                    value={form.carrozzeria}
                    onChange={(v) => set({ carrozzeria: v || null })}
                    options={Object.entries(VEHICLE_CARROZZERIA_LABELS)}
                    placeholder="—"
                    testId="comp-carrozzeria"
                  />
                </Field>
              </div>
            </div>
          </Card>

          {/* Sprint 4G.B2 — Card "Identificazione" (griglia 4/4/5) */}
          <Card className="p-4" data-testid="card-identificazione">
            <div className="font-heading text-sm uppercase text-slate-500 tracking-wide mb-3">Identificazione</div>

            {/* RIGA 1 — 4 colonne: [3 pulsanti placeholder Eurotax] | Targa | Telaio | Km */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-3">
              <div className="md:col-span-3">
                <Label className="text-xs text-slate-600">Ricerca dati</Label>
                <TooltipProvider delayDuration={150}>
                  <div className="mt-1 inline-flex rounded-md shadow-sm" role="group">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button type="button" variant="outline" size="sm" disabled
                                  data-testid="eurotax-search-targa"
                                  className="rounded-r-none border-r-0 opacity-60 cursor-not-allowed">
                            <Search className="h-3.5 w-3.5 mr-1" /> Targa
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>Disponibile con Eurotax</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button type="button" variant="outline" size="sm" disabled
                                  data-testid="eurotax-search-telaio"
                                  className="rounded-none border-r-0 opacity-60 cursor-not-allowed">
                            <ScanLine className="h-3.5 w-3.5 mr-1" /> Telaio
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>Disponibile con Eurotax</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button type="button" variant="outline" size="sm" disabled
                                  data-testid="eurotax-search-manuale"
                                  className="rounded-l-none opacity-60 cursor-not-allowed">
                            <ListChecks className="h-3.5 w-3.5 mr-1" /> Manuale
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>Disponibile con Eurotax</TooltipContent>
                    </Tooltip>
                  </div>
                </TooltipProvider>
              </div>
              <div className="md:col-span-3">
                <Field label="Targa" required={!form.flag_da_immatricolare} campo="targa">
                  <Input value={form.targa || ""}
                         onChange={(e) => set({ targa: (e.target.value || "").toUpperCase() })}
                         data-testid="ident-targa" />
                </Field>
              </div>
              <div className="md:col-span-3">
                <Field label="Telaio" campo="telaio">
                  <Input value={form.telaio || ""}
                         onChange={(e) => set({ telaio: (e.target.value || "").toUpperCase() })}
                         data-testid="ident-telaio" />
                </Field>
              </div>
              <div className="md:col-span-3">
                <Field label="Km" required campo="km">
                  <NumberInput mode="integer" thousandSeparator
                               value={form.km}
                               onChange={(v) => set({ km: v })}
                               testId="ident-km" />
                </Field>
              </div>
            </div>

            {/* RIGA 2 — 4 colonne: Marca | Modello | Versione | Allestimento */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
              <Field label="Marca" required>
                <Input value={form.marca || ""} onChange={(e) => set({ marca: e.target.value })} data-testid="ident-marca" />
              </Field>
              <Field label="Modello" required>
                <Input value={form.modello || ""} onChange={(e) => set({ modello: e.target.value })} data-testid="ident-modello" />
              </Field>
              <Field label="Versione">
                <Input value={form.versione || ""} onChange={(e) => set({ versione: e.target.value })} data-testid="ident-versione" />
              </Field>
              <Field label="Allestimento">
                <Input value={form.allestimento || ""} onChange={(e) => set({ allestimento: e.target.value })} data-testid="ident-allestimento" />
              </Field>
            </div>

            {/* Sprint 4G-PASS.1 — RIGA 3 a 4 colonne (12-grid): Prima immatricolazione |
                Nazione di provenienza (widget unico, Italia inclusa) | Immatric. Italia
                (cond) | N° proprietari */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-3">
                <Field label="Prima immatricolazione" required={!form.flag_da_immatricolare} campo="prima_immatricolazione">
                  <Input type="date" value={form.prima_immatricolazione || ""}
                         onChange={(e) => set({ prima_immatricolazione: e.target.value })} />
                </Field>
              </div>
              <div className="md:col-span-3">
                <Field label="Nazione di provenienza" required>
                  {/* Widget unico (Opzione A): un solo NazionePicker con includeItalia=true.
                      Quando l'utente sceglie IT → provenienza="italia" + nazione_provenienza=null.
                      Quando sceglie qualunque altra → provenienza="importazione" + nazione_provenienza=<code>.
                      Al load: se provenienza="italia" → mostriamo "IT" (Italia) anche se
                      nazione_provenienza ha un vestigio in DB. */}
                  <NazionePicker
                    value={
                      form.provenienza === "italia"
                        ? "IT"
                        : (form.nazione_provenienza || "")
                    }
                    includeItalia={true}
                    onChange={(code) => {
                      const c = (code || "").toUpperCase();
                      if (!c) {
                        // clear: torna a stato "non valorizzato" (entrambi null)
                        set({ provenienza: null, nazione_provenienza: null });
                      } else if (c === "IT") {
                        set({ provenienza: "italia", nazione_provenienza: null });
                      } else {
                        set({ provenienza: "importazione", nazione_provenienza: c });
                      }
                    }}
                    testId="vehicle-nazione-provenienza"
                    placeholder="Seleziona nazione…"
                  />
                </Field>
              </div>
              <div className="md:col-span-3">
                <Field label="Immatric. Italia">
                  <Input type="date"
                         value={form.seconda_immatricolazione || ""}
                         disabled={form.provenienza === "italia"}
                         onChange={(e) => set({ seconda_immatricolazione: e.target.value })}
                         data-testid="vehicle-seconda-immatricolazione" />
                </Field>
              </div>
              <div className="md:col-span-3">
                <Field label="N° proprietari" required={(form.stato || "").toLowerCase() !== "nuova"}>
                  <NumberInput mode="integer" value={form.n_proprietari}
                               onChange={(v) => set({ n_proprietari: v })} />
                </Field>
              </div>
            </div>
          </Card>

          {/* (Section vecchia "Identificazione" rimossa — sostituita dalla Card sopra) */}

          {/* Sprint 4G.B3 — Card "Caratteristiche / Motore / Colore" (griglia 4×6) */}
          <Card className="p-4" data-testid="card-caratteristiche">
            <div className="font-heading text-sm uppercase text-slate-500 tracking-wide mb-3">
              Caratteristiche / Motore / Colore
            </div>

            {/* RIGA 1 — 6 col: Alimentazione | Filtro antiparticolato | Tipo Carburante | Cambio | N° marce | Trazione */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-3">
              <div className="md:col-span-2">
                <Field label="Alimentazione" campo="carburante">
                  <ClearableSelect
                    value={form.carburante}
                    onChange={(v) => set({ carburante: v || null })}
                    options={Object.entries(VEHICLE_CARBURANTE_LABELS)}
                    testId="car-carburante"
                  />
                </Field>
              </div>
              <div className="md:col-span-2 flex items-end pb-2">
                <FlagCheck label="Filtro antiparticolato" testId="flag-filtro-antiparticolato"
                  checked={!!form.flag_filtro_antiparticolato}
                  onChange={(b) => set({ flag_filtro_antiparticolato: b })} />
              </div>
              <div className="md:col-span-2">
                <Field label="Tipo Carburante">
                  <ClearableSelect
                    value={form.tipo_carburante}
                    onChange={(v) => set({ tipo_carburante: v || null })}
                    options={Object.entries(VEHICLE_TIPO_CARBURANTE_LABELS)}
                    testId="car-tipo-carburante"
                  />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Cambio">
                  <ClearableSelect
                    value={form.cambio}
                    onChange={(v) => set({ cambio: v || null })}
                    options={Object.entries(VEHICLE_CAMBIO_LABELS)}
                    testId="car-cambio"
                  />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="N° marce">
                  <NumberInput mode="integer" value={form.n_marce}
                               onChange={(v) => set({ n_marce: v })} />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Trazione">
                  <ClearableSelect
                    value={form.trazione}
                    onChange={(v) => set({ trazione: v || null })}
                    options={Object.entries(VEHICLE_TRAZIONE_LABELS)}
                  />
                </Field>
              </div>
            </div>

            {/* RIGA 2 — 6 col: Colore Est | 2° colore | Tipo colore | Descrizione | Colore Interni | Materiale */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-3">
              <div className="md:col-span-2">
                <Field label="Colore Esterno" campo="colore">
                  <ClearableSelect
                    value={form.colore}
                    onChange={(v) => set({ colore: v || null })}
                    options={Object.entries(VEHICLE_COLORE_LABELS)}
                    swatches={VEHICLE_COLORE_HEX}
                    testId="car-colore"
                  />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="2° colore">
                  <ClearableSelect
                    value={form.secondo_colore}
                    onChange={(v) => set({ secondo_colore: v || null })}
                    options={Object.entries(VEHICLE_COLORE_LABELS)}
                    swatches={VEHICLE_COLORE_HEX}
                    testId="car-secondo-colore"
                  />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Tipo colore">
                  <ClearableSelect
                    value={form.tipo_colore}
                    onChange={(v) => set({ tipo_colore: v || null })}
                    options={Object.entries(VEHICLE_TIPO_COLORE_LABELS)}
                  />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Descrizione colore specifico">
                  <Input value={form.descrizione_colore || ""}
                         onChange={(e) => set({ descrizione_colore: e.target.value })}
                         data-testid="car-descrizione-colore" />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Colore Interni">
                  <ClearableSelect
                    value={form.colore_interni}
                    onChange={(v) => set({ colore_interni: v || null })}
                    options={Object.entries(VEHICLE_COLORE_INTERNI_LABELS)}
                    swatches={VEHICLE_COLORE_HEX}
                    testId="car-colore-interni"
                  />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Materiale">
                  <ClearableSelect
                    value={form.materiale_interni}
                    onChange={(v) => set({ materiale_interni: v || null })}
                    options={Object.entries(VEHICLE_MATERIALE_INTERNI_LABELS)}
                    testId="car-materiale-interni"
                  />
                </Field>
              </div>
            </div>

            {/* RIGA 3 — 6 col: numeri tecnici INTERI con separatore migliaia dove ha senso */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-3">
              <Field label="Cilindrata (cc)" campo="cilindrata">
                <NumberInput mode="integer" thousandSeparator
                             value={form.cilindrata}
                             onChange={(v) => set({ cilindrata: v })} />
              </Field>
              <Field label="N° cilindri">
                <NumberInput mode="integer" value={form.n_cilindri}
                             onChange={(v) => set({ n_cilindri: v })} />
              </Field>
              <Field label="cv fiscali">
                <NumberInput mode="integer" value={form.cv_fiscali}
                             onChange={(v) => set({ cv_fiscali: v })} />
              </Field>
              <Field label="Coppia max Nm">
                <NumberInput mode="integer" thousandSeparator
                             value={form.coppia_max}
                             onChange={(v) => set({ coppia_max: v })} />
              </Field>
              <Field label="Giri coppia max">
                <NumberInput mode="integer" thousandSeparator
                             value={form.giri_coppia_max}
                             onChange={(v) => set({ giri_coppia_max: v })} />
              </Field>
              <Field label="Giri pot. max">
                <NumberInput mode="integer" thousandSeparator
                             value={form.giri_potenza_max}
                             onChange={(v) => set({ giri_potenza_max: v })} />
              </Field>
            </div>

            {/* RIGA 4 — 6 col: 3 coppie bidirezionali kW↔CV (Termico / Elettrico / Tot) */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <PowerPair label="Mot termico (cv)" testId="car-mot-termico-cv"
                value={form.mot_termico_cv} unit="cv"
                onChange={(v) => setKwCvPair("mot_termico_kw", "mot_termico_cv", "cv", v, set)} />
              <PowerPair label="Mot termico (kW)" testId="car-mot-termico-kw"
                value={form.mot_termico_kw} unit="kw"
                onChange={(v) => setKwCvPair("mot_termico_kw", "mot_termico_cv", "kw", v, set)} />
              <PowerPair label="Mot elettrico (cv)" testId="car-mot-elettrico-cv"
                value={form.mot_elettrico_cv} unit="cv"
                onChange={(v) => setKwCvPair("mot_elettrico_kw", "mot_elettrico_cv", "cv", v, set)} />
              <PowerPair label="Mot elettrico (kW)" testId="car-mot-elettrico-kw"
                value={form.mot_elettrico_kw} unit="kw"
                onChange={(v) => setKwCvPair("mot_elettrico_kw", "mot_elettrico_cv", "kw", v, set)} />
              <PowerPair label="Cavalli Tot (cv)" testId="car-cavalli-tot-cv"
                value={form.cavalli_tot_cv} unit="cv"
                onChange={(v) => setKwCvPair("kw", "cavalli_tot_cv", "cv", v, set)} />
              <PowerPair label="Potenza Tot (kW)" testId="car-potenza-tot-kw" campo="kw"
                value={form.kw} unit="kw"
                onChange={(v) => setKwCvPair("kw", "cavalli_tot_cv", "kw", v, set)} />
            </div>
          </Card>

          {/* Sprint 4G.B4 — Card "Consumi / Emissioni / Dimensioni" (3 righe) */}
          <Card className="p-4" data-testid="card-consumi-dimensioni">
            <div className="font-heading text-sm uppercase text-slate-500 tracking-wide mb-3">
              Consumi / Emissioni / Dimensioni
            </div>
            {/* RIGA 1 — Consumi + Classe ambientale */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-3">
              <Field label="Urbano l/100 km · kWh/100 km">
                <NumberInput mode="decimal" decimals={2} thousandSeparator
                             value={form.consumo_urbano}
                             onChange={(v) => set({ consumo_urbano: v })}
                             testId="cons-urbano" />
              </Field>
              <Field label="Extra urbano l/100 km">
                <NumberInput mode="decimal" decimals={2} thousandSeparator
                             value={form.consumo_extraurbano}
                             onChange={(v) => set({ consumo_extraurbano: v })} />
              </Field>
              <Field label="Combinato l/100 km">
                <NumberInput mode="decimal" decimals={2} thousandSeparator
                             value={form.consumo_combinato}
                             onChange={(v) => set({ consumo_combinato: v })} />
              </Field>
              <Field label="Combinato GPL l/100 km">
                <NumberInput mode="decimal" decimals={2} thousandSeparator
                             value={form.consumo_combinato_gpl}
                             onChange={(v) => set({ consumo_combinato_gpl: v })} />
              </Field>
              <Field label="CO2 (g/km)">
                <NumberInput mode="decimal" decimals={2} thousandSeparator
                             value={form.co2}
                             onChange={(v) => set({ co2: v })}
                             testId="cons-co2" />
              </Field>
              <Field label="Classe Ambientale">
                <ClearableSelect
                  value={form.classe_ambientale}
                  onChange={(v) => set({ classe_ambientale: v || null })}
                  options={Object.entries(VEHICLE_CLASSE_AMBIENTALE_LABELS)}
                  testId="cons-classe-amb"
                />
              </Field>
            </div>
            {/* RIGA 2 — Dimensioni + Porte/Posti */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-3">
              <Field label="Lunghezza (cm)">
                <NumberInput mode="decimal" decimals={2}
                             value={form.lunghezza}
                             onChange={(v) => set({ lunghezza: v })} />
              </Field>
              <Field label="Larghezza (cm)">
                <NumberInput mode="decimal" decimals={2}
                             value={form.larghezza}
                             onChange={(v) => set({ larghezza: v })} />
              </Field>
              <Field label="Altezza (cm)">
                <NumberInput mode="decimal" decimals={2}
                             value={form.altezza}
                             onChange={(v) => set({ altezza: v })} />
              </Field>
              <Field label="N° porte">
                <NumberInput mode="integer" value={form.n_porte}
                             onChange={(v) => set({ n_porte: v })} />
              </Field>
              <Field label="Posti anteriori">
                <NumberInput mode="integer" value={form.posti_anteriori}
                             onChange={(v) => set({ posti_anteriori: v })} />
              </Field>
              <Field label="Posti totali">
                <NumberInput mode="integer" value={form.posti_totali}
                             onChange={(v) => set({ posti_totali: v })} />
              </Field>
            </div>
            {/* RIGA 3 — Pesi / Capacità (interi con separatore migliaia) */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Field label="Peso kg (massa a vuoto)">
                <NumberInput mode="integer" thousandSeparator
                             value={form.massa_vuoto}
                             onChange={(v) => set({ massa_vuoto: v })} />
              </Field>
              <Field label="Massa complessiva (kg)">
                <NumberInput mode="integer" thousandSeparator
                             value={form.massa_complessiva}
                             onChange={(v) => set({ massa_complessiva: v })} />
              </Field>
              <Field label="Capacità bagagliaio (l)">
                <NumberInput mode="integer" thousandSeparator
                             value={form.capacita_bagagliaio}
                             onChange={(v) => set({ capacita_bagagliaio: v })} />
              </Field>
              <Field label="Serbatoio (litri)">
                <NumberInput mode="integer" value={form.serbatoio_litri}
                             onChange={(v) => set({ serbatoio_litri: v })} />
              </Field>
            </div>
          </Card>

          {/* Sprint 4G.B4 — Card "Pneumatici" — 4 misure testo + 3 flag */}
          <Card className="p-4" data-testid="card-pneumatici">
            <div className="font-heading text-sm uppercase text-slate-500 tracking-wide mb-3">
              Pneumatici
            </div>
            {/* Riga campi: 4 misure + diametro cerchi + marca/modello (md:2 ciascuno = 12) */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-2">
                <Field label="Anteriori (di serie)">
                  <Input value={form.pneumatici_ant_serie || ""}
                         onChange={(e) => set({ pneumatici_ant_serie: e.target.value })}
                         placeholder="es. 185/55 R18 92H"
                         data-testid="pneu-ant-serie" />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Posteriori (di serie)">
                  <Input value={form.pneumatici_post_serie || ""}
                         onChange={(e) => set({ pneumatici_post_serie: e.target.value })}
                         placeholder="es. 205/45 R18 95H" />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Anteriori (al ritiro)">
                  <Input value={form.pneumatici_ant_ritiro || ""}
                         onChange={(e) => set({ pneumatici_ant_ritiro: e.target.value })} />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Posteriori (al ritiro)">
                  <Input value={form.pneumatici_post_ritiro || ""}
                         onChange={(e) => set({ pneumatici_post_ritiro: e.target.value })} />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Diametro cerchi (pollici)">
                  <NumberInput mode="integer" value={form.diametro_cerchi}
                               onChange={(v) => set({ diametro_cerchi: v })}
                               placeholder="es. 17"
                               data-testid="diametro-cerchi" />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Marca/modello pneum.">
                  <Input value={form.marca_modello_pneumatici || ""}
                         onChange={(e) => set({ marca_modello_pneumatici: e.target.value })}
                         placeholder="es. Michelin Primacy 4"
                         data-testid="marca-modello-pneumatici" />
                </Field>
              </div>
            </div>
            {/* Riga flag: Estivi / Invernali / 4 stagioni ORIZZONTALI */}
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2 mt-3">
              <FlagCheck label="Estivi" testId="flag-pneu-estivi"
                checked={!!form.flag_pneumatici_estivi}
                onChange={(b) => set({ flag_pneumatici_estivi: b })} />
              <FlagCheck label="Invernali" testId="flag-pneu-invernali"
                checked={!!form.flag_pneumatici_invernali}
                onChange={(b) => set({ flag_pneumatici_invernali: b })} />
              <FlagCheck label="4 stagioni" testId="flag-pneu-4stagioni"
                checked={!!form.flag_pneumatici_4stagioni}
                onChange={(b) => set({ flag_pneumatici_4stagioni: b })} />
            </div>
          </Card>

          {/* Sprint 4G.1-A — Scadenze ora INLINE (era tab separato) */}
          <Section title="Scadenze">
            <div className="col-span-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <ScadenzaCard title="Tassa proprietà (bollo)">
                <Field label="Scadenza"><Input type="date" value={form.scadenza_tassa_proprieta || ""} onChange={(e) => set({ scadenza_tassa_proprieta: e.target.value })} /></Field>
              </ScadenzaCard>
              <ScadenzaCard title="Revisione">
                <Field label="Ultima data"><Input type="date" value={form.ultima_revisione_data || ""} onChange={(e) => set({ ultima_revisione_data: e.target.value })} /></Field>
                <Field label="Ultima km"><NumberInput mode="integer" thousandSeparator value={form.ultima_revisione_km} onChange={(v) => set({ ultima_revisione_km: v })} /></Field>
                <Field label="Prossima data"><Input type="date" value={form.prossima_revisione_data || ""} onChange={(e) => set({ prossima_revisione_data: e.target.value })} /></Field>
              </ScadenzaCard>
              <ScadenzaCard title="Tagliando">
                <Field label="Ultima data"><Input type="date" value={form.ultimo_tagliando_data || ""} onChange={(e) => set({ ultimo_tagliando_data: e.target.value })} /></Field>
                <Field label="Tipo">
                  <Select value={form.ultimo_tagliando_tipo || ""} onValueChange={(v) => set({ ultimo_tagliando_tipo: v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{cardOptions(VEHICLE_TAGLIANDO_TIPO_LABELS)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Ultima km"><NumberInput mode="integer" thousandSeparator value={form.ultimo_tagliando_km} onChange={(v) => set({ ultimo_tagliando_km: v })} /></Field>
                <Field label="Prossima data"><Input type="date" value={form.prossimo_tagliando || ""} onChange={(e) => set({ prossimo_tagliando: e.target.value })} /></Field>
              </ScadenzaCard>
              <ScadenzaCard title="Garanzia casa madre">
                <Field label="Scadenza"><Input type="date" value={form.garanzia_casa_madre_scadenza || ""} onChange={(e) => set({ garanzia_casa_madre_scadenza: e.target.value })} /></Field>
              </ScadenzaCard>
              <ScadenzaCard title="Cinghia distribuzione">
                <Field label="Ultima data"><Input type="date" value={form.cinghia_distribuzione_data || ""} onChange={(e) => set({ cinghia_distribuzione_data: e.target.value })} /></Field>
                <Field label="Ultima km"><NumberInput mode="integer" thousandSeparator value={form.cinghia_distribuzione_km} onChange={(v) => set({ cinghia_distribuzione_km: v })} /></Field>
              </ScadenzaCard>
              {(form.carburante === "gpl" || form.carburante === "metano") && (
                <ScadenzaCard title="Bombole">
                  <Field label="Ultima revisione"><Input type="date" value={form.bombole_ultima_revisione || ""} onChange={(e) => set({ bombole_ultima_revisione: e.target.value })} /></Field>
                </ScadenzaCard>
              )}
            </div>
          </Section>

          <Section title="Note">
            <div className="col-span-full">
              <Textarea value={form.note} onChange={(e) => set({ note: e.target.value })} rows={3} />
            </div>
          </Section>
        </div>
      )}

      {tab === "accessori" && (
        <AccessoriDotazioniTab form={form} set={set} />
      )}

      {tab === "importi" && (
        <div className="space-y-4">
          {/* Sprint 4G.B5-FIX — CARD "Carico veicolo / Acquisto" rifinita su griglia 4/2/mandato */}
          <Card className="p-4" data-testid="section-carico-veicolo">
            <div className="font-heading text-sm uppercase text-slate-500 tracking-wide mb-3">Carico veicolo</div>

            {/* Sprint PERMUTA.1 — RIGA 1 (cond): flag "È una permuta" + selettore pratica + descrittiva + data arrivo */}
            <PermutaRow form={form} set={set} vehicle={vehicle} />

            {/* RIGA 2 — Prezzo | Tipo carico | Aliquota IVA | Imponibile|Imposta */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Field required label={
                (form.tipo_carico === "conto_terzi" || form.tipo_carico === "conto_terzi_iva")
                  ? "Prezzo ritiro pattuito"
                  : "Prezzo acquisto"
              }>
                {/* Sprint PERMUTA-UX C.3 — sfondo giallo (coerente con miniatto esente) */}
                <div className="rounded-md bg-amber-50 border border-amber-200 border-l-4 border-l-amber-500">
                  <CurrencyInput value={form.importi.prezzo_acquisto}
                    onChange={(v) => setI({ prezzo_acquisto: v })}
                    className="bg-transparent border-0 focus-visible:ring-0"
                    data-testid="carico-prezzo-acquisto" />
                </div>
              </Field>
              <Field label="Tipo di carico" required>
                <Select value={form.tipo_carico || ""} onValueChange={(v) => set({ tipo_carico: v })}>
                  <SelectTrigger data-testid="vehicle-tipo-carico-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{cardOptions(VEHICLE_TIPO_CARICO_LABELS)}</SelectContent>
                </Select>
              </Field>
              <Field label="Aliquota IVA">
                {TIPO_CARICO_SHOW_IVA.has(form.tipo_carico) ? (
                  <CodiceIvaPicker
                    value={form.iva_codice_carico}
                    onChange={(v) => set({ iva_codice_carico: v })}
                    testId="vehicle-carico-iva-codice"
                  />
                ) : (
                  <div className="px-3 py-2 rounded-md bg-slate-50 border border-slate-200 text-slate-400 text-xs italic"
                       data-testid="carico-iva-not-applicable">
                    {form.tipo_carico === "conto_terzi" ? "Conto terzi privato — nessuna IVA"
                      : form.tipo_carico === "conto_proprio_art36" ? "Regime margine art.36"
                      : "Seleziona Tipo di carico"}
                  </div>
                )}
              </Field>
              {/* Sprint 4G.B5-FIX — Imponibile | Imposta SEMPRE VISIBILE (anche CP non-IVA → mostra "—") */}
              <Field label="Imponibile | Imposta">
                <div className="px-3 py-2 rounded-md bg-slate-50 border border-slate-200 text-sm text-slate-700"
                     data-testid="carico-imponibile-imposta">
                  {calcImporti.imponibile != null ? (
                    <span>
                      <CurrencyDisplay className="font-medium text-slate-900" value={calcImporti.imponibile} />
                      <span className="mx-2 text-slate-400">|</span>
                      <CurrencyDisplay className="font-medium text-slate-900" value={calcImporti.imposta} />
                    </span>
                  ) : (
                    <span className="text-slate-400 italic">—</span>
                  )}
                </div>
              </Field>
            </div>

            {form.tipo_carico === "conto_terzi" && (
              <div className="mt-2 text-xs text-slate-500 italic">
                Veicolo in conto terzi da privato: nessuna IVA sul veicolo. Le commissioni saranno fatturate separatamente.
              </div>
            )}

            {/* RIGA 3 — Fornitore | Cointestatario */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 pt-4 border-t border-slate-200">
              <AnagraficaPicker
                label="Fornitore"
                required
                helper="Anagrafica con categoria 'Fornitore veicoli'."
                value={form.supplier_id || null}
                onChange={(id) => set({ supplier_id: id || "" })}
                filterCategoria="fornitore_veicoli"
                excludeId={form.cointestatario_id || null}
                contextCompanyId={form.company_id || null}
                testIdPrefix="fornitore-picker"
              />
              <AnagraficaPicker
                label="Cointestatario"
                helper="Disponibile sia per Conto Proprio che per Conto Terzi."
                value={form.cointestatario_id || null}
                onChange={(id) => set({ cointestatario_id: id || "" })}
                filterCategoria={null}
                excludeId={form.supplier_id || null}
                contextCompanyId={form.company_id || null}
                testIdPrefix="cointestatario-picker"
              />
            </div>

            {/* RIGA 4 — Mandato (conditional CP/CT) + Giorni scadenza + Giacenza */}
            {(form.tipo_carico === "conto_proprio_art36" || form.tipo_carico === "conto_proprio_iva") && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 pt-4 border-t border-slate-200">
                <Field label="Data atto" required={!form.flag_da_immatricolare}>
                  <Input type="date" value={form.data_atto || ""}
                         onChange={(e) => set({ data_atto: e.target.value })}
                         data-testid="data_atto-input" />
                </Field>
                <div className="md:col-span-2">
                  <GiacenzaPreview form={form} vehicle={vehicle} />
                </div>
              </div>
            )}
            {(form.tipo_carico === "conto_terzi" || form.tipo_carico === "conto_terzi_iva") && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <MandatoFields
                      inizio={form.inizio_mandato}
                      fine={form.fine_mandato}
                      onChangeInizio={(v) => set({ inizio_mandato: v })}
                      onChangeFine={(v) => set({ fine_mandato: v })}
                      flagDaImmatricolare={!!form.flag_da_immatricolare}
                    />
                  </div>
                  {form.fine_mandato && (
                    <div>
                      <Label className="text-xs text-slate-600">Giorni scadenza mandato</Label>
                      <div className={`mt-1 px-3 py-2 rounded-md border text-right tabular-nums ${
                        _giorniScadenzaMandato(form.fine_mandato) < 0
                          ? "bg-rose-50 border-rose-200 text-rose-700"
                          : "bg-slate-50 border-slate-200 text-slate-700"
                      }`} data-testid="giorni-scadenza-mandato">
                        {_giorniScadenzaMandato(form.fine_mandato)} gg
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-3">
                  <GiacenzaPreview form={form} vehicle={vehicle} />
                </div>
              </div>
            )}
          </Card>

          {/* Sprint 4G.B5-FIX — CARD "Vendita" rifinita su griglia 4/3 (senza sotto-blocchi affiancati) */}
          <Card className="p-4" data-testid="card-vendita">
            <div className="font-heading text-sm uppercase text-slate-500 tracking-wide mb-3">Vendita</div>

            {/* RIGA 1 — Margine | Prezzo B2B | Prezzo Vendita | Prezzo WEB */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Field label="Margine minimo" required>
                {/* Sprint PERMUTA-UX C.3 — sfondo verde chiaro */}
                <div className="rounded-md bg-emerald-50 border border-emerald-200 border-l-4 border-l-emerald-500">
                  <CurrencyInput value={form.importi.margine_minimo}
                    onChange={(v) => setI({ margine_minimo: v })}
                    className="bg-transparent border-0 focus-visible:ring-0"
                    data-testid="vendita-margine-minimo" />
                </div>
              </Field>
              <Field label="Prezzo B2B">
                {(() => {
                  const destList = Array.isArray(form.destinazione_commerciale)
                    ? form.destinazione_commerciale : ["vendita"];
                  const b2bEnabled = destList.includes("b2b_commercianti");
                  return b2bEnabled ? (
                    <div className="border-l-4 border-fuchsia-400 pl-2 -ml-2 rounded">
                      <CurrencyInput value={form.importi.prezzo_b2b}
                        onChange={(v) => setI({ prezzo_b2b: v })}
                        data-testid="prezzo-b2b-input" />
                    </div>
                  ) : (
                    <div className="px-3 py-2 rounded-md bg-slate-50 border border-slate-200 text-right text-slate-400 text-xs italic"
                         data-testid="prezzo-b2b-disabled">
                      Attiva "B2B-Commercianti" in Destinazione
                    </div>
                  );
                })()}
              </Field>
              <Field label="Prezzo di Vendita">
                <div className="px-3 py-2 rounded-md bg-sky-50 border border-sky-200 border-l-4 border-l-sky-400 text-right"
                     data-testid="vendita-prezzo-vendita-derivato">
                  <CurrencyDisplay className="font-medium text-sky-900" value={calcImporti.prezzoVendMin} />
                </div>
              </Field>
              <Field label="Prezzo WEB">
                {/* Sprint PERMUTA-UX C.3 — sfondo viola chiaro */}
                <div className="rounded-md bg-purple-50 border border-purple-200 border-l-4 border-l-purple-500">
                  <CurrencyInput value={form.importi.prezzo_web}
                    onChange={(v) => setI({ prezzo_web: v })}
                    className="bg-transparent border-0 focus-visible:ring-0"
                    data-testid="vendita-prezzo-web" />
                </div>
              </Field>
            </div>

            {/* RIGA 2 — Destinazione Commerciale | Listino | Sconto */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 pt-4 border-t border-slate-200">
              <div>
                <Label className="text-xs text-slate-600">Destinazione Commerciale</Label>
                <div className="mt-1 flex flex-wrap gap-3 px-2 py-2 rounded-md bg-slate-50 border border-slate-200"
                     data-testid="destinazione-commerciale-group">
                  {[
                    ["vendita", "Vendita"],
                    ["noleggio", "Noleggio"],
                    ["b2b_commercianti", "B2B Commercianti"],
                    ["uso_interno", "Uso interno"],
                  ].map(([k, label]) => {
                    const destList = Array.isArray(form.destinazione_commerciale)
                      ? form.destinazione_commerciale : ["vendita"];
                    const checked = destList.includes(k);
                    return (
                      <FlagCheck key={k} label={label}
                        testId={`dest-${k}`}
                        checked={checked}
                        onChange={(b) => {
                          let next = destList.filter((x) => x !== k);
                          if (b) next.push(k);
                          if (next.length === 0) next = ["vendita"];
                          set({ destinazione_commerciale: next });
                        }} />
                    );
                  })}
                </div>
              </div>
              <Field label="Listino casa madre">
                <CurrencyInput value={form.importi.listino}
                  onChange={(v) => setI({ listino: v })}
                  data-testid="vendita-listino" />
              </Field>
              <Field label="Sconto € prezzo web da listino">
                {calcImporti.scontoEur != null ? (
                  <div className="px-3 py-2 rounded-md bg-slate-50 border border-slate-200 text-right text-slate-700"
                       data-testid="sconto-eur-display">
                    <CurrencyDisplay value={calcImporti.scontoEur} />
                    {calcImporti.sconto != null && (
                      <span className="ml-2 text-[11px] text-slate-500" data-testid="sconto-perc-display">
                        ({calcImporti.sconto.toString().replace(".", ",")}%)
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="px-3 py-2 rounded-md bg-slate-50 border border-slate-200 text-right text-slate-400 text-xs italic">
                    —
                  </div>
                )}
              </Field>
            </div>
          </Card>

          {/* Sprint 4I1.B1 — SPESE VEICOLO (sostituisce Approntamento + SpeseERipristini) */}
          <SpeseVeicoloTable
            vehicleId={id}
            companyId={form.company_id}
            importi={form.importi}
            onReload={() => reloadVehicle()} />

          {/* SEZIONE 5: Riepilogo calcoli (Sprint 4C L5: totale costo auto a destra) */}
          <Card className="p-4 bg-slate-50/50">
            <div className="font-heading text-sm uppercase text-slate-500 tracking-wide mb-3">Riepilogo calcoli</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 border-l-4 border-l-amber-500 text-right"
                   data-testid="riep-totale-costo-auto">
                <div className="text-[11px] uppercase text-amber-700 tracking-wide">Totale costo auto</div>
                <div className="text-2xl font-bold text-amber-900 mt-1">
                  <CurrencyDisplay value={calcImporti.totale} />
                </div>
              </div>
              <div className="p-3 rounded-md bg-white border border-slate-200">
                <div className="text-[11px] uppercase text-slate-500 tracking-wide">Prezzo web - Totale Costi</div>
                <div className={`text-xl font-medium mt-1 ${
                  calcImporti.delta > 0 ? "text-emerald-700" :
                  calcImporti.delta < 0 ? "text-red-600" : "text-slate-700"
                }`}>
                  <CurrencyDisplay value={calcImporti.delta} />
                </div>
              </div>
              <div className="p-3 rounded-md bg-white border border-slate-200">
                <div className="text-[11px] uppercase text-slate-500 tracking-wide">Recupero su promo</div>
                <div className={`text-xl font-medium mt-1 flex items-center gap-2 ${
                  calcImporti.recupero > 0 ? "text-red-600" : "text-emerald-700"
                }`}>
                  {calcImporti.recupero > 0 && <AlertTriangle className="h-4 w-4" />}
                  <CurrencyDisplay value={calcImporti.recupero} />
                </div>
              </div>
            </div>
          </Card>

          {/* Sprint §92 — Ordine: Compensi (CT) → Dati acquisto → Dati ritiro → Note */}
          {(form.tipo_carico === "conto_terzi" || form.tipo_carico === "conto_terzi_iva") && (
            <CompensiIntermediazioneCard form={form} set={set} vehicle={vehicle} />
          )}
          <DatiAcquistoCard form={form} set={set} />
          <DatiRitiroCard form={form} set={set} />

          {/* SEZIONE 6: Note */}
          <Section title="Note importi">
            <div className="col-span-full">
              <Textarea value={form.importi.note_importi || ""} onChange={(e) => setI({ note_importi: e.target.value })} rows={3} />
            </div>
          </Section>
        </div>
      )}

      {tab === "scadenze" && (
        // Sprint 4G.1-A: tab Scadenze rimosso dalla barra tabs; le card sono
        // state spostate INLINE dentro tab="dati". Questo blocco resta come
        // fallback no-op se qualcuno apre con ?tab=scadenze nel link vecchio.
        <Card className="p-6 text-center text-slate-500">
          La sezione "Scadenze" è ora dentro la scheda <strong>Dati Veicolo</strong>.
        </Card>
      )}

      {tab === "foto" && vehicle?.id && (
        <VehicleFotoTab
          vehicleId={vehicle.id}
          canEdit={!vehicleLocked}
          onCountChange={(n) => setVehicle((v) => v ? { ...v, n_foto: n } : v)}
        />
      )}

      {tab === "web" && vehicle?.id && (
        <VehicleWebTab
          vehicle={vehicle}
          form={form}
          set={set}
          canEdit={!vehicleLocked}
          onReload={() => reloadVehicle()}
        />
      )}

      {tab === "documenti" && vehicle?.id && (
        <DocumentsTab
          ownerType="vehicle"
          ownerId={vehicle.id}
          companyId={vehicle.company_id}
          canUpload={true}
          canDelete={elev}
        />
      )}

      {tab === "storico" && elev && (
        <Card className="p-4">
          {audit.length === 0 ? (
            <div className="text-center text-slate-500 py-8">Nessuna voce storico disponibile.</div>
          ) : (
            <ul className="space-y-2 text-sm">
              {audit.map((a, idx) => {
                // Sprint 2B.2-B: evidenza speciale per cambi disponibilità
                const isTransition = a.action === "vehicle.status_transition";
                if (isTransition) {
                  const meta = a.meta || {};
                  const fromStatus = meta.from_status || a.before?.status;
                  const toStatus = meta.to_status || a.after?.status;
                  const kind = meta.kind || "";
                  const motivo = meta.motivo || "";
                  const borderColor = kind === "regression" ? "border-rose-300"
                    : kind === "forward" ? "border-blue-300" : "border-emerald-300";
                  return (
                    <li key={idx} className={`border-l-2 ${borderColor} pl-3 py-2`} data-testid={`audit-transition-${idx}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">
                          Cambio disponibilità
                        </span>
                        <DisponibilitaBadge status={fromStatus} />
                        <span className="text-slate-400">→</span>
                        <DisponibilitaBadge status={toStatus} />
                        {kind === "regression" && (
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200">
                            Regressione
                          </span>
                        )}
                        {kind === "free" && (
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">
                            Libera
                          </span>
                        )}
                        {kind === "forward" && (
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 border border-blue-200">
                            Avanti
                          </span>
                        )}
                      </div>
                      {motivo && (
                        <div className="text-xs text-slate-600 mt-1 italic">"{motivo}"</div>
                      )}
                      <div className="text-xs text-slate-500 mt-1">
                        {a.user_email || a.user_id} · {a.timestamp}
                      </div>
                    </li>
                  );
                }
                // Sprint 4A.7: rendering automatico (grouped/forced/legacy)
                return <AuditRowAuto key={idx} entry={a} idx={idx} />;
              })}
            </ul>
          )}
        </Card>
      )}

      {tab === "preventivi" && (
        <PreventiviTab vehicleId={id} vehicleStatus={vehicle?.status} vehicleCompanyId={vehicle?.company_id} onGoTo={goTo} />
      )}

      {tab === "vendita" && (
        associatedVendita
          ? <VenditaDetailContent venditaId={associatedVendita.id} hideHeader={true} onGoTo={goTo} />
          : <EmptyVendita
              vehicleStatus={vehicle?.status}
              onSwitchToPreventivi={() => setTab("preventivi")}
            />
      )}

      {currentTab?.placeholder && (
        <Card className="p-12 text-center">
          {currentTab.icon && React.createElement(currentTab.icon, { className: "h-12 w-12 text-slate-300 mx-auto mb-3" })}
          <div className="text-slate-700 font-medium">{currentTab.label}</div>
          <div className="text-slate-500 text-sm mt-1">{currentTab.placeholder}</div>
        </Card>
      )}
      </fieldset>
      {/* Sprint CRM-6 — fuori dal blocco della scheda: le lead si consultano anche a veicolo bloccato */}
      {tab === "lead" && lead.length > 0 && vehicle?.company_id && (
        <LeadVeicolo items={lead} vehicleId={id} companyId={vehicle.company_id} />
      )}

      <RiallineamentoCaricoDialog esito={riallineamento} onClose={() => setRiallineamento(null)} />
      <StampaMandatoDialog
        open={openStampa}
        onOpenChange={setOpenStampa}
        vehicle={vehicle}
        praticaId={associatedVendita?.id}
        onGoTo={goTo}
      />

      <CambioDisponibilitaModal
        open={openCambio}
        onOpenChange={setOpenCambio}
        vehicle={vehicle}
        onUpdated={async (v) => {
          // Sprint 4A.3: refresh completo veicolo + vendita associata + auto-switch tab
          setVehicle(v);
          setForm({ ...emptyForm(), ...v, importi: { ...emptyForm().importi, ...(v.importi || {}) } });
          try {
            const r = await api.get(`/vendite?vehicle_id=${id}&page_size=10`);
            const arr = (r.data?.items || []).filter((x) => x.status !== "annullata");
            const newVendita = arr[0] || null;
            setAssociatedVendita(newVendita);
            // Auto-switch al tab Vendita se ne è appena nata una (impegnata o venduta)
            if (newVendita && (v?.status === "RESERVED" || v?.status === "SOLD")) {
              setTab("vendita");
              setSearchParams({ tab: "vendita" });
            }
          } catch {
            setAssociatedVendita(null);
          }
        }}
      />
      <GuardModal />
      <Conflitti esito={esitoConflitti} mieiValori={mieiValori} onApplica={applicaConflitti}
        onChiudi={() => { setEsitoConflitti(null); toast.message("Tenuti i valori attuali sui campi in conflitto"); }} />
      {/* Sprint 4G.B6 — Popup primo salvataggio: scelta consapevole disponibilità fisica */}
      <Dialog open={dispPopupOpen} onOpenChange={() => { /* non chiudibile via X */ }}>
        <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()}
                       onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Disponibilità fisica del veicolo</DialogTitle>
            <DialogDescription>
              Conferma o modifica la disponibilità fisica del veicolo appena
              creato. Questa scelta non riapparirà ai salvataggi successivi.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {[
              { val: "VIRTUAL", label: "Virtuale",
                desc: "Il veicolo esiste solo a documento (es. nuovo da immatricolare)" },
              { val: "INCOMING", label: "In arrivo",
                desc: "Il veicolo arriverà fisicamente in sede (es. permuta, import)" },
              { val: "AVAILABLE", label: "Disponibile",
                desc: "Il veicolo è fisicamente già in sede o disponibile alla presa visione" },
            ].map((opt) => (
              <label key={opt.val}
                className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer
                  ${dispChoice === opt.val ? "border-blue-500 bg-blue-50/50" : "border-slate-200 hover:bg-slate-50"}`}
                data-testid={`disp-first-save-${opt.val.toLowerCase()}`}>
                <input type="radio" name="disp-first-save" value={opt.val}
                       checked={dispChoice === opt.val}
                       onChange={() => setDispChoice(opt.val)}
                       className="mt-0.5" />
                <div className="text-sm">
                  <div className="font-medium">{opt.label}</div>
                  <div className="text-xs text-slate-600">{opt.desc}</div>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={confirmDispFisicaFirstSave}
                    data-testid="disp-first-save-confirm">
              Conferma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================== Helpers UI =====================
function Section({ title, subtitle, children }) {
  return (
    <Card className="p-4">
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <div className="font-heading text-sm uppercase text-slate-500 tracking-wide">{title}</div>
        {subtitle && (
          <div className="text-[11px] uppercase text-slate-400 tracking-wide" data-testid={`section-${title.toLowerCase().replace(/[^a-z]+/g, '-')}-subtitle`}>
            {subtitle}
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{children}</div>
    </Card>
  );
}
function Field({ label, required, children, campo }) {
  return (
    <div data-campo={campo}>
      <Label className="text-xs text-slate-600">{label}{required && <span className="text-red-500"> *</span>}</Label>
      {children}
    </div>
  );
}

// Sprint 4G.B5 — Giorni alla scadenza del mandato (Conto Terzi).
// Ritorna intero firmato: > 0 = mandato ancora valido, < 0 = scaduto da N giorni,
// 0 = scade oggi. null/undefined se fine_mandato non valorizzato.
function _giorniScadenzaMandato(fineMandatoISO) {
  if (!fineMandatoISO) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(fineMandatoISO);
  if (Number.isNaN(end.getTime())) return 0;
  end.setHours(0, 0, 0, 0);
  return Math.round((end - today) / (1000 * 60 * 60 * 24));
}

// Sprint 4G.B3 — Flag checkbox compatto (riga 2 card Competenza e tipo)

// Sprint STAMPE (§92) — Tab "Accessori e Dotazioni": spunte manuali + dati ritiro.
// Il contenitore `dotazioni_extra` ospita SOLO le spunte manuali; il catalogo
// tecnico (futuro) vivrà in un blocco separato per non mescolare le due fonti.
const DOTAZIONI_FLAGS = [
  ["libretto_istruzioni", "Libretto istruzioni e manutenzione"],
  ["carnet_tagliandi", "Carnet tagliandi"],
  ["ruota_scorta", "Ruota di scorta"],
  ["ruotino_scorta", "Ruotino di scorta"],
  ["copricerchi", "Copricerchi"],
  ["catene_neve", "Catene da neve"],
  ["gomme_neve", "Gomme da neve"],
  ["estintore", "Estintore"],
  ["tappetini", "Tappetini"],
  ["serbatoio_adblue", "Serbatoio con AdBlue"],
  ["documenti_gpl", "Documenti GPL"],
  ["borsa_attrezzi_cric", "Borsa attrezzi e cric"],
  ["antenna", "Antenna"],
  ["kit_gonfiaggio", "Kit di gonfiaggio"],
  ["kit_fusibili", "Kit fusibili"],
  ["kit_lampadine", "Kit lampadine"],
  ["kit_pronto_soccorso", "Kit pronto soccorso"],
  ["kit_etilometrico", "Kit etilometrico"],
  ["giubbino_emergenza", "Giubbino di emergenza"],
  ["cappelliera", "Cappelliera"],
  ["triangolo_emergenza", "Triangolo di emergenza"],
  ["cd_scheda_navigatore", "CD o scheda navigatore"],
  ["chiave_master", "Chiave master"],
  ["gancio_traino", "Gancio traino"],
  ["rete_animali", "Rete fissa per animali"],
  ["cavo_ricarica", "Cavo di ricarica"],
  ["borsa_cavo_ricarica", "Borsa per cavo di ricarica"],
  ["cavo_adattatore", "Cavo adattatore"],
  ["caricatore_portatile", "Caricatore portatile"],
  ["compressore_portatile", "Compressore portatile"],
  ["wallbox", "Wallbox"],
  ["dash_cam", "Dash cam"],
  ["telecamera_posteriore", "Telecamera posteriore"],
  ["codice_avviamento", "Codice avviamento"],
  ["impianto_gas", "Impianto GAS"],
  ["barre_tetto", "Barre da tetto"],
  ["box_tetto", "Box da tetto"],
  ["porta_bici", "Porta bici"],
  ["porta_sci_snowboard", "Porta sci e snowboard"],
  ["marchiatura_vetri", "Marchiatura vetri"],
  ["doppie_chiavi", "Doppie chiavi"],
];
const RITIRO_MOTIVO = { conto_vendita: "Conto vendita", acquisto: "Acquisto", permuta: "Permuta" };
const RITIRO_CARBURANTE = {
  vuoto: "Vuoto", riserva: "Riserva", un_quarto: "Un quarto",
  meta: "Metà", tre_quarti: "Tre quarti", pieno: "Pieno",
};
const RITIRO_ASSICURAZIONE = { proprietario: "Del proprietario", agenzia: "Dell'Agenzia" };
const RITIRO_DOCUMENTI = [
  ["carta_circolazione", "Carta di circolazione / documento unico"],
  ["certificato_proprieta", "Certificato di proprietà"],
  ["certificato_garanzia", "Certificato di garanzia"],
  ["tagliandi_documentazione", "Tagliandi e documentazione manutenzione"],
  ["chiavi", "Chiavi"],
];

function AccessoriDotazioniTab({ form, set }) {
  const de = form.dotazioni_extra && typeof form.dotazioni_extra === "object" ? form.dotazioni_extra : {};
  const setDE = (patch) => set({ dotazioni_extra: { ...de, ...patch } });
  return (
    <div className="space-y-4" data-testid="tab-accessori-content">
      {/* Riquadro 1 — Dotazioni aggiuntive e speciali */}
      <Card className="p-4" data-testid="card-dotazioni-extra">
        <div className="font-heading text-sm uppercase text-slate-500 tracking-wide mb-1">Dotazioni aggiuntive e speciali</div>
        <p className="text-xs text-slate-400 mb-3">Spunte manuali. Gli accessori dal catalogo tecnico verranno gestiti separatamente.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
          {DOTAZIONI_FLAGS.map(([k, label]) => (
            <FlagCheck key={k} label={label} testId={`dotazione-${k.replace(/_/g, "-")}`}
              checked={!!de[k]} onChange={(b) => setDE({ [k]: b })} />
          ))}
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 pt-4 border-t border-slate-200">
          <Field label="Code card">
            <Input value={de.code_card || ""} onChange={(e) => setDE({ code_card: e.target.value })}
              data-testid="dotazione-code-card" />
          </Field>
          <Field label="Autoradio">
            <Input value={de.autoradio || ""} onChange={(e) => setDE({ autoradio: e.target.value })}
              data-testid="dotazione-autoradio" />
          </Field>
          <Field label="Block system">
            <Input value={de.block_system || ""} onChange={(e) => setDE({ block_system: e.target.value })}
              data-testid="dotazione-block-system" />
          </Field>
        </div>
      </Card>
    </div>
  );
}

// Sprint STAMPE (§92) — Card "Dati del ritiro" (spostata in "Carico e importi").
function DatiRitiroCard({ form, set }) {
  const dr = form.dati_ritiro && typeof form.dati_ritiro === "object" ? form.dati_ritiro : {};
  const drDocs = dr.documenti_ricevuti && typeof dr.documenti_ricevuti === "object" ? dr.documenti_ricevuti : {};
  const setDR = (patch) => set({ dati_ritiro: { ...dr, ...patch } });
  const setDRDocs = (patch) => set({ dati_ritiro: { ...dr, documenti_ricevuti: { ...drDocs, ...patch } } });
  return (
      <Card className="p-4" data-testid="card-dati-ritiro">
        <div className="font-heading text-sm uppercase text-slate-500 tracking-wide mb-3">Dati del ritiro</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Motivo">
            <Select value={dr.motivo || ""} onValueChange={(v) => setDR({ motivo: v })}>
              <SelectTrigger data-testid="ritiro-motivo"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {Object.entries(RITIRO_MOTIVO).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Data e ora ritiro">
            <Input type="datetime-local" value={dr.data_ora || ""} onChange={(e) => setDR({ data_ora: e.target.value })}
              data-testid="ritiro-data-ora" />
          </Field>
          <Field label="Chilometraggio al ritiro">
            <NumberInput mode="integer" thousandSeparator value={dr.km_ritiro}
              onChange={(v) => setDR({ km_ritiro: v })} data-testid="ritiro-km" />
          </Field>
          <Field label="Livello carburante">
            <Select value={dr.livello_carburante || ""} onValueChange={(v) => setDR({ livello_carburante: v })}>
              <SelectTrigger data-testid="ritiro-carburante"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {Object.entries(RITIRO_CARBURANTE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Numero chiavi ricevute">
            <NumberInput mode="integer" value={dr.numero_chiavi}
              onChange={(v) => setDR({ numero_chiavi: v })} data-testid="ritiro-numero-chiavi" />
          </Field>
          <Field label="Assicurazione durante il deposito">
            <Select value={dr.assicurazione_deposito || ""} onValueChange={(v) => setDR({ assicurazione_deposito: v })}>
              <SelectTrigger data-testid="ritiro-assicurazione"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {Object.entries(RITIRO_ASSICURAZIONE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <div className="mt-4 pt-4 border-t border-slate-200">
          <Label className="text-xs text-slate-600">Documenti ricevuti</Label>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2" data-testid="ritiro-documenti-group">
            {RITIRO_DOCUMENTI.map(([k, label]) => (
              <FlagCheck key={k} label={label} testId={`ritiro-doc-${k.replace(/_/g, "-")}`}
                checked={!!drDocs[k]} onChange={(b) => setDRDocs({ [k]: b })} />
            ))}
          </div>
        </div>
        <div className="mt-4">
          <Field label="Note sullo stato">
            <Textarea value={dr.note_stato || ""} onChange={(e) => setDR({ note_stato: e.target.value })} rows={3}
              data-testid="ritiro-note-stato" />
          </Field>
        </div>
      </Card>
  );
}


// Sprint §92 fix — Card "Compensi e dettagli intermediazione" (solo conto terzi),
// sotto il riepilogo calcoli. Calcolo euro↔percentuale SOLO lato server, invocato
// on-blur (senza salvare la scheda) via /vehicles/compenso/preview.
function ValoreProposto({ valore, impostato, testid }) {
  if (valore == null) return <p className="text-[11px] text-slate-400 mt-1" data-testid={testid}>Nessun valore proposto nelle impostazioni azienda</p>;
  return (
    <p className="text-[11px] text-slate-500 mt-1" data-testid={testid}>
      {impostato ? `Proposto dall'azienda: ${formatEUR(valore)} — sovrascritto su questo mandato`
                 : `Valore proposto dall'azienda: ${formatEUR(valore)} (si stampa questo se il campo resta vuoto)`}
    </p>
  );
}

function CompensiIntermediazioneCard({ form, set, vehicle }) {
  const ci = form.compenso_intermediazione && typeof form.compenso_intermediazione === "object" ? form.compenso_intermediazione : {};
  const md = form.mandato_dettagli && typeof form.mandato_dettagli === "object" ? form.mandato_dettagli : {};
  const tipo = ci.tipo || "differenza";
  const setCI = (patch) => set({ compenso_intermediazione: { ...ci, ...patch } });
  const setMD = (patch) => set({ mandato_dettagli: { ...md, ...patch } });
  const esclusiva = md.esclusiva === undefined ? true : !!md.esclusiva;
  const prezzoAcquisto = form.importi?.prezzo_acquisto;
  const [netto, setNetto] = React.useState(vehicle?.compenso_intermediazione?.netto_preponente ?? null);
  // Sprint 4 — valori proposti dall'azienda (impostazioni → parametri contrattuali), sovrascrivibili qui.
  const [pcAz, setPcAz] = React.useState({});
  React.useEffect(() => {
    if (!form.company_id) return;
    api.get(`/companies/${form.company_id}`).then((r) => setPcAz(r.data?.parametri_contrattuali || {})).catch(() => setPcAz({}));
  }, [form.company_id]);
  // Ref sempre allineato all'ultimo stato: evita closure obsolete nei blur/change.
  const ciRef = React.useRef(ci);
  ciRef.current = ci;
  // Contatore di refresh: incrementato DOPO ogni ricalcolo (che avviene sempre a
  // campo sfocato). Forza il refresh del display di entrambi i campi senza mai
  // rimontare quello che l'utente sta digitando (refreshKey è stabile durante la
  // digitazione e sul focus).
  const [refreshKey, setRefreshKey] = React.useState(0);

  const recompute = async (nextCI) => {
    const pa = prezzoAcquisto === "" || prezzoAcquisto == null ? null : Number(prezzoAcquisto);
    try {
      const r = await api.post("/vehicles/compenso/preview", {
        prezzo_acquisto: pa, compenso_intermediazione: nextCI,
      });
      const d = r.data || {};
      set({ compenso_intermediazione: {
        ...nextCI, tipo: d.tipo ?? nextCI.tipo,
        percentuale: d.percentuale ?? null, euro: d.euro ?? null,
      } });
      setNetto(d.netto_preponente ?? null);
      setRefreshKey((k) => k + 1);
    } catch { /* non bloccare l'editing in caso di errore di rete */ }
  };

  const changeTipo = (t) => recompute({ ...ciRef.current, tipo: t });
  const _num = (x) => (x === "" || x == null ? NaN : Number(x));

  return (
    <Card className="p-4" data-testid="card-compensi-intermediazione">
      <div className="font-heading text-sm uppercase text-slate-500 tracking-wide mb-3">Compensi e dettagli intermediazione</div>
      {/* Riga 1 — Compensi */}
      <Label className="text-xs text-slate-600 font-medium">Compensi</Label>
      <div className="mt-2 grid grid-cols-1 md:grid-cols-5 gap-3">
        <Field label="Tipo di compenso">
          <Select value={tipo} onValueChange={changeTipo}>
            <SelectTrigger data-testid="compenso-tipo"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="differenza">Differenza</SelectItem>
              <SelectItem value="percentuale">Percentuale</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Compenso (%)">
          {/* NumberInput emette onChange ad ogni tasto → ricalcolo on-blur sul ref aggiornato.
              key = refreshKey: stabile durante la digitazione, si rinfresca dopo il ricalcolo. */}
          <div onBlur={() => tipo === "percentuale" && recompute(ciRef.current)}>
            <NumberInput key={`perc-${refreshKey}`}
              mode="decimal" value={ci.percentuale} disabled={tipo !== "percentuale"}
              onChange={(v) => setCI({ percentuale: v, ultimo_campo: "percentuale" })}
              testId="compenso-percentuale" />
          </div>
        </Field>
        <Field label="Compenso (€)">
          {/* CurrencyInput emette onChange SOLO al blur col valore fresco → ricalcolo qui,
              solo se il valore è cambiato (evita di sovrascrivere l'ultimo campo digitato).
              key = refreshKey: stabile durante la digitazione, si rinfresca dopo il ricalcolo. */}
          <CurrencyInput key={`euro-${refreshKey}`}
            value={ci.euro} disabled={tipo !== "percentuale"}
            onChange={(v) => {
              if (tipo !== "percentuale") return;
              if (_num(v) === _num(ciRef.current.euro)) return;
              recompute({ ...ciRef.current, euro: v, ultimo_campo: "euro" });
            }}
            data-testid="compenso-euro" />
        </Field>
        <Field label="Costo del deposito (€)">
          <CurrencyInput value={md.costo_deposito} onChange={(v) => setMD({ costo_deposito: v })}
            data-testid="mandato-costo-deposito" />
        </Field>
        <Field label="Periodo deposito">
          <Select value={md.periodo_deposito || ""} onValueChange={(v) => setMD({ periodo_deposito: v })}>
            <SelectTrigger data-testid="mandato-periodo-deposito"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="giorno">Al giorno</SelectItem>
              <SelectItem value="mese">Al mese</SelectItem>
              <SelectItem value="forfettario">Forfettario</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <div className="mt-2 flex items-center gap-2 text-xs text-slate-500" data-testid="compenso-netto-preponente">
        <span>Netto spettante al preponente:</span>
        {netto != null
          ? <CurrencyDisplay className="font-medium text-slate-800" value={netto} />
          : <span className="italic text-slate-400">—</span>}
      </div>

      {/* Riga 2 — Dettagli mandato */}
      <Label className="text-xs text-slate-600 font-medium mt-4 block">Dettagli mandato</Label>
      <div className="mt-2 grid grid-cols-1 md:grid-cols-5 gap-3">
        <Field label="Esclusiva">
          <Select value={esclusiva ? "si" : "no"} onValueChange={(v) => setMD({ esclusiva: v === "si" })}>
            <SelectTrigger data-testid="mandato-esclusiva"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="si">Sì</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Penale per revoca anticipata (€)">
          <CurrencyInput value={md.penale_revoca} onChange={(v) => setMD({ penale_revoca: v })}
            placeholder={pcAz.penale_revoca_mandato != null ? formatEUR(pcAz.penale_revoca_mandato) : "0,00"}
            data-testid="mandato-penale-revoca" />
          <ValoreProposto valore={pcAz.penale_revoca_mandato} impostato={md.penale_revoca != null && md.penale_revoca !== ""}
            testid="mandato-penale-revoca-proposto" />
        </Field>
        <Field label="Compenso vendita diretta del proprietario (€)">
          <CurrencyInput value={md.compenso_vendita_diretta} onChange={(v) => setMD({ compenso_vendita_diretta: v })}
            placeholder={pcAz.compenso_vendita_diretta_mandato != null ? formatEUR(pcAz.compenso_vendita_diretta_mandato) : "0,00"}
            data-testid="mandato-compenso-vendita-diretta" />
          <ValoreProposto valore={pcAz.compenso_vendita_diretta_mandato}
            impostato={md.compenso_vendita_diretta != null && md.compenso_vendita_diretta !== ""}
            testid="mandato-compenso-vendita-diretta-proposto" />
        </Field>
        <Field label="Firma fuori dai locali commerciali">
          <Select value={md.firma_fuori_locali ? "si" : "no"} onValueChange={(v) => setMD({ firma_fuori_locali: v === "si" })}>
            <SelectTrigger data-testid="mandato-firma-fuori-locali"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="si">Sì</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Deposito fino al">
          <Input type="date" value={md.data_fine_deposito || ""} onChange={(e) => setMD({ data_fine_deposito: e.target.value || null })}
            data-testid="mandato-data-fine-deposito" />
        </Field>
      </div>
    </Card>
  );
}

// Sprint STAMPE (§92) — Dati dell'acquisto (da privato), in fondo a "Carico e importi".
function DatiAcquistoCard({ form, set }) {
  const da = form.dati_acquisto && typeof form.dati_acquisto === "object" ? form.dati_acquisto : {};
  const setDA = (patch) => set({ dati_acquisto: { ...da, ...patch } });
  return (
    <Card className="p-4" data-testid="card-dati-acquisto">
      <div className="font-heading text-sm uppercase text-slate-500 tracking-wide mb-3">Dati acquisto</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Modalità di pagamento">
          <Select value={da.modalita_pagamento || ""} onValueChange={(v) => setDA({ modalita_pagamento: v })}>
            <SelectTrigger data-testid="acquisto-modalita-pagamento"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="contanti">Contanti</SelectItem>
              <SelectItem value="bonifico">Bonifico</SelectItem>
              <SelectItem value="assegno_circolare">Assegno circolare</SelectItem>
              <SelectItem value="misto">Misto</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Momento del pagamento">
          <Select value={da.momento_pagamento || ""} onValueChange={(v) => setDA({ momento_pagamento: v })}>
            <SelectTrigger data-testid="acquisto-momento-pagamento"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="contestuale">Contestuale</SelectItem>
              <SelectItem value="differito">Differito</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        {da.momento_pagamento === "differito" && (
          <Field label="Data del pagamento">
            <Input type="date" value={da.data_pagamento || ""} onChange={(e) => setDA({ data_pagamento: e.target.value })}
              data-testid="acquisto-data-pagamento" />
          </Field>
        )}
        {da.modalita_pagamento === "bonifico" && (
          <Field label="IBAN del venditore">
            <Input value={da.iban_venditore || ""} onChange={(e) => setDA({ iban_venditore: e.target.value })}
              data-testid="acquisto-iban-venditore" />
          </Field>
        )}
        <Field label="Importo estinzione finanziamento (€)">
          <CurrencyInput value={da.importo_estinzione_finanziamento}
            onChange={(v) => setDA({ importo_estinzione_finanziamento: v })}
            data-testid="acquisto-importo-estinzione" />
        </Field>
        {da.importo_estinzione_finanziamento != null && da.importo_estinzione_finanziamento !== "" && (
          <Field label="Ente finanziario">
            <Input value={da.ente_finanziario || ""} onChange={(e) => setDA({ ente_finanziario: e.target.value })}
              data-testid="acquisto-ente-finanziario" />
          </Field>
        )}
      </div>
      <div className="mt-4">
        <Field label="Dichiarazioni particolari del venditore">
          <Textarea value={da.dichiarazioni_venditore || ""} onChange={(e) => setDA({ dichiarazioni_venditore: e.target.value })}
            rows={3} data-testid="acquisto-dichiarazioni" />
        </Field>
      </div>
    </Card>
  );
}



// Sprint PERMUTA-UX (PARTE B+C) — Riga "È una permuta" nella card Carico veicolo.
// 4 colonne: [flag permuta] [bottone seleziona pratica] [descrizione pratica
// origine (codice visibile + dati VEICOLO della pratica origine)]
// [Data previsto arrivo].
// La data previsto arrivo è SEMPRE visibile per veicoli con disponibilità
// fisica VIRTUAL o INCOMING (anche non-permuta) — vedi C.4.
function PermutaRow({ form, set, vehicle }) {
  const isPermuta = !!form.vendita_origine_id;
  const [showLink, setShowLink] = useState(isPermuta);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Sprint PERMUTA-UX (PARTE B) — Pratica origine "fetched" per mostrare
  // codice VISIBILE + dati VEICOLO di quella pratica (non dell'auto corrente).
  const [origineInfo, setOrigineInfo] = useState(null); // {codice, snap}
  useEffect(() => { setShowLink(!!form.vendita_origine_id); }, [form.vendita_origine_id]);
  useEffect(() => {
    if (!form.vendita_origine_id) { setOrigineInfo(null); return; }
    let cancelled = false;
    api.get(`/vendite/${form.vendita_origine_id}`)
      .then((r) => { if (!cancelled) setOrigineInfo({
          codice: r.data?.codice,
          snap: r.data?.vehicle_snapshot || {},
          cliente: r.data?.cliente || {},
        }); })
      .catch(() => { if (!cancelled) setOrigineInfo({ codice: null, snap: {}, cliente: {} }); });
    return () => { cancelled = true; };
  }, [form.vendita_origine_id]);

  const handlePickVendita = (ven) => {
    set({ vendita_origine_id: ven.id });
    toast.success(`Pratica ${ven.codice || ven.id} collegata`);
    setPickerOpen(false);
  };

  // Sprint PERMUTA-UX C.4 — Data previsto arrivo SEMPRE visibile per
  // VIRTUAL/INCOMING (anche non-permuta).
  const dispFisica = vehicle?.disponibilita_fisica || vehicle?.status || form.status;
  const showDataArrivo = isPermuta || dispFisica === "VIRTUAL" || dispFisica === "INCOMING";

  // Descrizione pratica origine (codice + veicolo della pratica)
  const snap = origineInfo?.snap || {};
  const cli = origineInfo?.cliente || {};
  const cliName = cli.ragione_sociale
    || `${cli.cognome || ""} ${cli.nome || ""}`.trim()
    || null;
  const snapDesc = `${snap.marca || ""} ${snap.modello || ""} ${snap.versione || ""}`.trim();

  return (
    <div className="mb-3 pb-3 border-b border-dashed border-slate-200">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-start">
        {/* col1 — Flag permuta */}
        <div className="flex items-center gap-2 pt-1.5">
          <FlagCheck label="È una permuta" testId="flag-is-permuta"
            checked={showLink}
            onChange={(b) => {
              setShowLink(b);
              if (!b) set({ vendita_origine_id: null });
            }} />
        </div>
        {/* col2 — Bottone seleziona/cambia pratica */}
        <div className="pt-1">
          {showLink ? (
            <Button type="button" variant="outline" size="sm"
              onClick={() => setPickerOpen(true)}
              className="w-full justify-start"
              data-testid="permuta-select-vendita">
              <Search className="h-4 w-4 mr-1" />
              {form.vendita_origine_id ? "Cambia pratica…" : "Seleziona pratica…"}
            </Button>
          ) : (
            <div className="text-xs text-slate-400 italic px-2 py-1.5">—</div>
          )}
        </div>
        {/* col3 — Descrizione pratica origine */}
        <div>
          {showLink && form.vendita_origine_id ? (
            <div className="px-3 py-2 rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-700"
                 data-testid="permuta-descrittiva">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-medium text-slate-500 uppercase tracking-wide">Da pratica</span>
                <span className="font-mono text-blue-700">{origineInfo?.codice || "…"}</span>
              </div>
              <div className="text-slate-600 mt-0.5">
                {snapDesc || "—"}
                {snap.targa && (
                  <span className="font-mono text-slate-700 ml-2">· {snap.targa}</span>
                )}
              </div>
              {cliName && (
                <div className="text-[11px] text-slate-500 mt-0.5">cliente: {cliName}</div>
              )}
            </div>
          ) : (
            <div className="text-xs text-slate-400 italic px-2 py-1.5">—</div>
          )}
        </div>
        {/* col4 — Data previsto arrivo (visibile per permuta o VIRTUAL/INCOMING) */}
        <div>
          {showDataArrivo ? (
            <Field label="Data previsto arrivo">
              <Input type="date" value={form.data_previsto_arrivo || ""}
                     onChange={(e) => set({ data_previsto_arrivo: e.target.value })}
                     data-testid="permuta-data-arrivo" />
            </Field>
          ) : (
            <div className="text-xs text-slate-400 italic px-2 py-1.5">—</div>
          )}
        </div>
      </div>
      <VenditaPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={handlePickVendita}
        excludeId={null}
        contextCompanyId={form.company_id || null}
        testIdPrefix="permuta-vendita-picker"
      />
    </div>
  );
}


// Sprint 4G.B3 — Input compatto per coppia kW↔CV con NumberInput.
// Il NumberInput emette un numero pulito (o null); setKwCvPair lo riceve raw.
// Sprint SCHEDA-VEICOLO — targa con l'estetica della targa. Stelle UE e «I» solo sul formato
// italiano attuale (AA000AA); bande lisce negli altri casi; banda destra sempre liscia.
const TARGA_ITALIANA = /^[A-Z]{2}\d{3}[A-Z]{2}$/;
export function TargaVeicolo({ targa }) {
  if (!targa) {
    return (
      <span className="inline-flex items-center justify-center px-3 py-0.5 rounded border border-slate-700 bg-white font-mono font-bold text-base tracking-[0.18em] text-slate-900 uppercase shadow-sm"
            data-testid="vehicle-targa-stilizzata" title="Targa non assegnata">— — —</span>
    );
  }
  const italiana = TARGA_ITALIANA.test(targa.replace(/\s/g, ""));
  return (
    <span className="inline-flex items-stretch h-8 rounded-[5px] border-2 border-slate-800 bg-white overflow-hidden shadow-sm"
          data-testid="vehicle-targa-stilizzata" data-formato={italiana ? "italiana" : "altro"} title={`Targa: ${targa}`}>
      <span className="w-5 bg-[#003399] flex flex-col items-center justify-end pb-0.5 shrink-0" data-testid="targa-banda-sx">
        {italiana && (
          <>
            <svg viewBox="0 0 20 20" className="h-3 w-3" aria-hidden="true">
              {Array.from({ length: 12 }, (_, k) => {
                const a = (k * Math.PI) / 6;
                return <circle key={k} cx={10 + 7 * Math.sin(a)} cy={10 - 7 * Math.cos(a)} r="1.3" fill="#FFCC00" />;
              })}
            </svg>
            <span className="text-[9px] font-bold leading-none text-white" data-testid="targa-sigla-i">I</span>
          </>
        )}
      </span>
      <span className="px-2.5 flex items-center font-mono font-bold text-lg tracking-[0.14em] text-slate-900 uppercase whitespace-nowrap">
        {targa}
      </span>
      <span className="w-5 bg-[#003399] shrink-0" data-testid="targa-banda-dx" />
    </span>
  );
}

function PowerPair({ label, value, unit, onChange, testId, campo }) {
  return (
    <div data-campo={campo}>
      <Label className="text-xs text-slate-600">{label}</Label>
      <div className="relative">
        <NumberInput
          mode="integer"
          thousandSeparator
          value={value}
          onChange={(n) => onChange(n)}
          testId={testId}
          className="pr-9"
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase text-slate-400 pointer-events-none">
          {unit}
        </span>
      </div>
    </div>
  );
}

// Sprint 4G.B3 — Helper bidirezionale kW↔CV.
//   Formula: kW = CV × 0,7355 ; CV = kW × 1,3596 (arrotondamento a intero).
//   `kwField`/`cvField` sono i nomi tecnici dei due campi nel form.
//   `source` = "kw" | "cv" indica quale campo l'utente sta digitando.
//   Anti-loop intrinseco: viene invocato solo su input event utente; la
//   propagazione setta entrambi i campi in un singolo `set()` (no useEffect).
function setKwCvPair(kwField, cvField, source, raw, setFn) {
  const s = (raw ?? "").toString().trim();
  if (s === "") {
    setFn({ [source === "kw" ? kwField : cvField]: null });
    return;
  }
  const n = Number(s);
  if (Number.isNaN(n)) return;
  const intVal = Math.round(n);
  if (source === "kw") {
    setFn({ [kwField]: intVal, [cvField]: Math.round(intVal * 1.3596) });
  } else {
    setFn({ [cvField]: intVal, [kwField]: Math.round(intVal * 0.7355) });
  }
}
function ScadenzaCard({ title, children }) {
  return (
    <Card className="p-3">
      <div className="font-heading text-sm font-medium text-slate-700 mb-2">{title}</div>
      <div className="grid grid-cols-2 gap-2">{children}</div>
    </Card>
  );
}
function Stat({ label, value, children, highlight, positive, negative }) {
  const color = highlight ? "text-slate-900 font-bold text-lg" : positive ? "text-emerald-700 font-medium" : negative ? "text-red-600 font-medium" : "text-slate-700";
  return (
    <div className="p-3 rounded-md bg-slate-50 border border-slate-100">
      <div className="text-[11px] uppercase text-slate-500 tracking-wide">{label}</div>
      {value ? <div className={`mt-1 ${color}`}>{value}</div> : <div className="mt-1">{children}</div>}
    </div>
  );
}
function SpeseTableUnified({ label, itemsKey, items, suppliers, onAdd, onChange, onRemove, canMarkPaid }) {
  // Subtotali per stato
  const sub = (filterFn) => items.filter(filterFn).reduce((s, x) => s + (Number(x.importo) || 0), 0);
  const subDaFare = sub((x) => (x.stato || "da_fare") === "da_fare");
  const subEseguito = sub((x) => x.stato === "eseguito");
  const subPagato = sub((x) => x.stato === "pagato");
  const totale = subDaFare + subEseguito + subPagato;

  const statoOptions = [
    { v: "da_fare", l: "Da fare" },
    { v: "eseguito", l: "Eseguito" },
    { v: "pagato", l: "Pagato", needsIncassi: true },
  ];

  return (
    <Card className="p-4" data-testid={`spese-${itemsKey}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-heading text-sm uppercase text-slate-500 tracking-wide">{label}</div>
        <Button variant="outline" size="sm" onClick={onAdd} data-testid={`spese-${itemsKey}-add`}>
          <Plus className="h-3 w-3 mr-1" /> Aggiungi
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-4 text-center">Nessuna voce.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase text-slate-500">
              <tr>
                <th className="text-left p-1 min-w-[160px]">Descrizione</th>
                <th className="text-right p-1 w-32">Importo</th>
                <th className="text-left p-1 w-44">Fornitore</th>
                <th className="text-left p-1 w-32">Data</th>
                <th className="text-left p-1 min-w-[140px]">Note</th>
                <th className="text-left p-1 w-32">Stato</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const stato = it.stato || "da_fare";
                return (
                  <tr key={i} className="border-t border-slate-100 align-top">
                    <td className="p-1">
                      <Input
                        value={it.descrizione || ""}
                        onChange={(e) => onChange(i, { descrizione: e.target.value })}
                        data-testid={`spese-${itemsKey}-${i}-desc`}
                      />
                    </td>
                    <td className="p-1">
                      <CurrencyInput
                        value={it.importo}
                        onChange={(v) => onChange(i, { importo: v })}
                      />
                    </td>
                    <td className="p-1">
                      <Select value={it.supplier_id || ""} onValueChange={(v) => onChange(i, { supplier_id: v || null })}>
                        <SelectTrigger className="h-9"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          {suppliers.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.ragione_sociale || `${s.cognome} ${s.nome}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-1">
                      <Input type="date" value={it.data || ""} onChange={(e) => onChange(i, { data: e.target.value })} />
                    </td>
                    <td className="p-1">
                      <Input
                        value={it.note || ""}
                        onChange={(e) => onChange(i, { note: e.target.value })}
                        placeholder="—"
                      />
                    </td>
                    <td className="p-1">
                      <Select value={stato} onValueChange={(v) => onChange(i, { stato: v })}>
                        <SelectTrigger className="h-9" data-testid={`spese-${itemsKey}-${i}-stato`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statoOptions.map((opt) => {
                            const disabled = opt.needsIncassi && !canMarkPaid && stato !== opt.v;
                            return (
                              <SelectItem
                                key={opt.v}
                                value={opt.v}
                                disabled={disabled}
                                title={disabled ? "Richiede permesso Gestione incassi" : undefined}
                              >
                                {opt.l}{disabled ? " 🔒" : ""}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-1 text-right">
                      <button
                        onClick={() => onRemove(i)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`spese-${itemsKey}-${i}-remove`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Subtotali per stato + totale (Sprint 4C L4: no sfondo, solo testo colorato) */}
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <div className="px-3 py-2 rounded-md border border-slate-200" data-testid={`spese-${itemsKey}-sub-dafare`}>
          <div className="text-[10px] uppercase tracking-wide text-blue-700">Da fare</div>
          <div className="font-medium text-blue-700 mt-0.5">{formatCurrencyIT(subDaFare)}</div>
        </div>
        <div className="px-3 py-2 rounded-md border border-slate-200" data-testid={`spese-${itemsKey}-sub-eseguito`}>
          <div className="text-[10px] uppercase tracking-wide text-red-700">Eseguito</div>
          <div className="font-medium text-red-700 mt-0.5">{formatCurrencyIT(subEseguito)}</div>
        </div>
        <div className="px-3 py-2 rounded-md border border-slate-200" data-testid={`spese-${itemsKey}-sub-pagato`}>
          <div className="text-[10px] uppercase tracking-wide text-green-800">Pagato</div>
          <div className="font-medium text-green-800 mt-0.5">{formatCurrencyIT(subPagato)}</div>
        </div>
        <div className="px-3 py-2 rounded-md border border-slate-300 border-l-4 border-l-slate-400" data-testid={`spese-${itemsKey}-sub-totale`}>
          <div className="text-[10px] uppercase tracking-wide text-slate-700">Totale</div>
          <div className="font-semibold text-slate-900 mt-0.5">{formatCurrencyIT(totale)}</div>
        </div>
      </div>
    </Card>
  );
}

// Sprint 4C (L3) — Sezione unificata Spese + Ripristini (1 tabella, 2 array DB)
function SpeseERipristiniUnificata({
  spese_generiche, spese_ripristino, suppliers,
  onAddGen, onAddRip, onChangeGen, onChangeRip, onRemoveGen, onRemoveRip,
  onMoveGenToRip, onMoveRipToGen, canMarkPaid,
}) {
  // Normalizzo tutte le voci con una flag di categoria
  const allItems = [
    ...spese_generiche.map((it, idx) => ({ ...it, __cat: "generica", __idx: idx })),
    ...spese_ripristino.map((it, idx) => ({ ...it, __cat: "ripristino", __idx: idx })),
  ];

  const n = (v) => Number(v) || 0;
  const totGen = spese_generiche.reduce((s, x) => s + n(x.importo), 0);
  const totRip = spese_ripristino.reduce((s, x) => s + n(x.importo), 0);
  const totale = totGen + totRip;

  // Subtotali per stato (aggregati su entrambi gli array)
  const sub = (filterFn) => allItems.filter(filterFn).reduce((s, x) => s + n(x.importo), 0);
  const subDaFare = sub((x) => (x.stato || "da_fare") === "da_fare");
  const subEseguito = sub((x) => x.stato === "eseguito");
  const subPagato = sub((x) => x.stato === "pagato");

  const statoOptions = [
    { v: "da_fare", l: "Da fare" },
    { v: "eseguito", l: "Eseguito" },
    { v: "pagato", l: "Pagato", needsIncassi: true },
  ];

  const handleChange = (row, patch) => {
    if (row.__cat === "generica") onChangeGen(row.__idx, patch);
    else onChangeRip(row.__idx, patch);
  };
  const handleRemove = (row) => {
    if (row.__cat === "generica") onRemoveGen(row.__idx);
    else onRemoveRip(row.__idx);
  };
  const handleCatChange = (row, newCat) => {
    if (row.__cat === newCat) return;
    if (newCat === "generica") onMoveRipToGen(row.__idx);
    else onMoveGenToRip(row.__idx);
  };

  return (
    <Card className="p-4" data-testid="section-spese-e-ripristini">
      <div className="flex items-center justify-between mb-3">
        <div className="font-heading text-sm uppercase text-slate-500 tracking-wide">Spese e ripristini</div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onAddGen} data-testid="add-spesa-generica">
            <Plus className="h-3 w-3 mr-1" /> Generica
          </Button>
          <Button variant="outline" size="sm" onClick={onAddRip} data-testid="add-spesa-ripristino">
            <Plus className="h-3 w-3 mr-1" /> Ripristino
          </Button>
        </div>
      </div>

      {allItems.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-4 text-center">Nessuna voce.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase text-slate-500">
              <tr>
                <th className="text-left p-1 w-28">Categoria</th>
                <th className="text-left p-1 min-w-[160px]">Descrizione</th>
                <th className="text-right p-1 w-32">Importo</th>
                <th className="text-left p-1 w-44">Fornitore</th>
                <th className="text-left p-1 w-32">Data</th>
                <th className="text-left p-1 min-w-[120px]">Note</th>
                <th className="text-left p-1 w-32">Stato</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {allItems.map((row) => {
                const stato = row.stato || "da_fare";
                const statoColor = stato === "pagato" ? "text-green-800"
                  : stato === "eseguito" ? "text-red-700"
                  : "text-blue-700";
                return (
                  <tr key={`${row.__cat}-${row.__idx}`} className="border-t border-slate-100 align-top"
                      data-testid={`unified-row-${row.__cat}-${row.__idx}`}>
                    <td className="p-1">
                      <Select value={row.__cat} onValueChange={(v) => handleCatChange(row, v)}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="generica">Generica</SelectItem>
                          <SelectItem value="ripristino">Ripristino</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-1">
                      <Input value={row.descrizione || ""}
                        onChange={(e) => handleChange(row, { descrizione: e.target.value })}
                        data-testid={`unified-desc-${row.__cat}-${row.__idx}`} />
                    </td>
                    <td className={`p-1 ${statoColor}`}>
                      <CurrencyInput value={row.importo}
                        onChange={(v) => handleChange(row, { importo: v })} />
                    </td>
                    <td className="p-1">
                      <Select value={row.supplier_id || ""}
                              onValueChange={(v) => handleChange(row, { supplier_id: v || null })}>
                        <SelectTrigger className="h-9"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          {suppliers.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.ragione_sociale || `${s.cognome} ${s.nome}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-1">
                      <Input type="date" value={row.data || ""}
                        onChange={(e) => handleChange(row, { data: e.target.value })} />
                    </td>
                    <td className="p-1">
                      <Input value={row.note || ""}
                        onChange={(e) => handleChange(row, { note: e.target.value })}
                        placeholder="—" />
                    </td>
                    <td className={`p-1 ${statoColor} font-medium`}>
                      <Select value={stato} onValueChange={(v) => handleChange(row, { stato: v })}>
                        <SelectTrigger className="h-9" data-testid={`unified-stato-${row.__cat}-${row.__idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statoOptions.map((opt) => {
                            const disabled = opt.needsIncassi && !canMarkPaid && stato !== opt.v;
                            return (
                              <SelectItem key={opt.v} value={opt.v} disabled={disabled}
                                title={disabled ? "Richiede permesso Gestione incassi" : undefined}>
                                {opt.l}{disabled ? " 🔒" : ""}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-1 text-right">
                      <button onClick={() => handleRemove(row)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`unified-remove-${row.__cat}-${row.__idx}`}>
                        <X className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Subtotali: per categoria + per stato + totale (Sprint 4C L4: no sfondo) */}
      <div className="mt-3 space-y-1.5 text-sm">
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-600" data-testid="spese-subtotali-categoria">
          <span>Totale spese generiche: <span className="font-medium text-slate-900">{formatCurrencyIT(totGen)}</span></span>
          <span>Totale spese ripristino: <span className="font-medium text-slate-900">{formatCurrencyIT(totRip)}</span></span>
          <span className="font-semibold">TOTALE: <span className="text-slate-900">{formatCurrencyIT(totale)}</span></span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs border-t border-slate-100 pt-2">
          <div className="px-2 py-1 border border-slate-200 rounded" data-testid="unified-sub-dafare">
            <span className="text-[10px] uppercase tracking-wide text-blue-700">Da fare</span>
            <div className="font-medium text-blue-700">{formatCurrencyIT(subDaFare)}</div>
          </div>
          <div className="px-2 py-1 border border-slate-200 rounded" data-testid="unified-sub-eseguito">
            <span className="text-[10px] uppercase tracking-wide text-red-700">Eseguito</span>
            <div className="font-medium text-red-700">{formatCurrencyIT(subEseguito)}</div>
          </div>
          <div className="px-2 py-1 border border-slate-200 rounded" data-testid="unified-sub-pagato">
            <span className="text-[10px] uppercase tracking-wide text-green-800">Pagato</span>
            <div className="font-medium text-green-800">{formatCurrencyIT(subPagato)}</div>
          </div>
        </div>
      </div>
    </Card>
  );
}


// ============================================================
// Sprint 2B.2-A — Componenti sezione "Carico veicolo"
// ============================================================

function fmtItDate(iso) {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  return y && m && d ? `${d}/${m}/${y}` : s;
}

function MandatoFields({ inizio, fine, onChangeInizio, onChangeFine, flagDaImmatricolare = false }) {
  // Default: selettore mesi (più rapido). Se l'utente apre con un fine non corrispondente
  // a un valore canonico, comunque mostra "Data libera".
  const MESI_OPTS = [1, 2, 3, 6, 9, 12, 18, 24, 36, 48];
  const computeMonths = () => {
    if (!inizio || !fine) return 12;
    try {
      const i = new Date(inizio);
      const f = new Date(fine);
      const months = (f.getFullYear() - i.getFullYear()) * 12 + (f.getMonth() - i.getMonth());
      return MESI_OPTS.includes(months) ? months : null;
    } catch { return null; }
  };
  const detectedMonths = computeMonths();
  const [mode, setMode] = useState(detectedMonths != null ? "mesi" : "data");
  const [mesi, setMesi] = useState(detectedMonths ?? 12);

  const applyMonths = (n) => {
    setMesi(n);
    if (!inizio) return;
    const start = new Date(inizio);
    const end = new Date(start);
    end.setMonth(end.getMonth() + n);
    onChangeFine(end.toISOString().slice(0, 10));
  };

  // When inizio_mandato changes, recompute fine if in "mesi" mode
  useEffect(() => {
    if (mode === "mesi" && inizio) {
      const start = new Date(inizio);
      const end = new Date(start);
      end.setMonth(end.getMonth() + mesi);
      onChangeFine(end.toISOString().slice(0, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inizio]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <Field label="Inizio mandato" required={!flagDaImmatricolare}>
        <Input type="date" value={inizio || ""} onChange={(e) => onChangeInizio(e.target.value)} data-testid="inizio_mandato-input" />
      </Field>
      <div className="md:col-span-2">
        <div className="flex items-center justify-between mb-1">
          <Label className="text-xs uppercase tracking-wide text-slate-500">Fine mandato</Label>
          <div className="inline-flex rounded border border-slate-200 text-[11px] overflow-hidden">
            <button type="button"
              onClick={() => setMode("mesi")}
              className={`px-2 py-0.5 ${mode === "mesi" ? "bg-blue-600 text-white" : "bg-white text-slate-600"}`}
              data-testid="mandato-mode-mesi">Selettore mesi</button>
            <button type="button"
              onClick={() => setMode("data")}
              className={`px-2 py-0.5 ${mode === "data" ? "bg-blue-600 text-white" : "bg-white text-slate-600"}`}
              data-testid="mandato-mode-data">Data libera</button>
          </div>
        </div>
        {mode === "mesi" ? (
          <div className="flex items-center gap-2">
            <Select value={String(mesi)} onValueChange={(v) => applyMonths(Number(v))}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MESI_OPTS.map((m) => <SelectItem key={m} value={String(m)}>{m} mesi</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-sm text-slate-500">→ {fmtItDate(fine)}</span>
          </div>
        ) : (
          <Input type="date" value={fine || ""} onChange={(e) => onChangeFine(e.target.value)} data-testid="fine_mandato-input" />
        )}
      </div>
    </div>
  );
}

function GiacenzaPreview({ form, vehicle }) {
  // Calcola giorni in giacenza con stessa logica del backend
  const tc = form.tipo_carico;
  const isCP = tc === "conto_proprio_art36" || tc === "conto_proprio_iva";
  const isCT = tc === "conto_terzi" || tc === "conto_terzi_iva";
  let src = null;
  let srcLabel = "data carico";
  if (isCP && form.data_atto) { src = form.data_atto; srcLabel = "data atto"; }
  else if (isCT && form.inizio_mandato) { src = form.inizio_mandato; srcLabel = "inizio mandato"; }
  else { src = vehicle?.data_carico || form.data_carico; srcLabel = "data carico"; }
  let days = null;
  if (src) {
    try {
      const d = new Date(src);
      const today = new Date();
      const diff = Math.max(0, Math.floor((today - d) / (1000 * 60 * 60 * 24)));
      days = diff;
    } catch { days = null; }
  }
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 flex items-center justify-between">
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500">Giorni in giacenza</div>
        <div className="text-lg font-semibold text-slate-900" data-testid="giorni-giacenza-value">
          {days != null ? `${days} giorni` : "—"}
        </div>
      </div>
      <div className="text-[11px] text-slate-400 italic">Calcolato da: {srcLabel}</div>
    </div>
  );
}

function DepositoSelect({ value, onChange, companyId, sites }) {
  // value: null | {tipo, sede_id}
  const v = value || {};
  // Build a single string identifier for Select (uncontrolled-friendly)
  const currentVal =
    v.tipo === "sede" && v.sede_id ? `sede:${v.sede_id}` :
    v.tipo === "in_uso_proprietario" ? "in_uso_proprietario" :
    v.tipo === "dal_fornitore" ? "dal_fornitore" : "";

  const handle = (val) => {
    if (!val) { onChange(null); return; }
    if (val.startsWith("sede:")) {
      onChange({ tipo: "sede", sede_id: val.slice(5) });
    } else {
      onChange({ tipo: val, sede_id: null });
    }
  };

  return (
    <Select value={currentVal} onValueChange={handle}>
      <SelectTrigger data-testid="deposito-select"><SelectValue placeholder="—" /></SelectTrigger>
      <SelectContent>
        {companyId && sites.length > 0 && (
          <>
            <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400 border-b">
              Presso le nostre sedi
            </div>
            {sites.filter((s) => s.attiva !== false).map((s) => (
              <SelectItem key={s.id} value={`sede:${s.id}`}>c/o {s.nome}</SelectItem>
            ))}
          </>
        )}
        <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400 border-b mt-1">
          Esterno
        </div>
        <SelectItem value="in_uso_proprietario">In uso al Proprietario</SelectItem>
        <SelectItem value="dal_fornitore">Da Azienda Fornitore</SelectItem>
      </SelectContent>
    </Select>
  );
}


function CaricoWarningBanner({ warning, onGoToField }) {
  return (
    <div
      className="rounded-md bg-amber-50 border border-amber-300 text-amber-900 px-4 py-3 flex items-start gap-3"
      data-testid={`warning-banner-${warning.code}`}
    >
      <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm">{warning.title}</div>
        <div className="text-sm mt-0.5">{warning.message}</div>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onGoToField}
        className="border-amber-400 hover:bg-amber-100 text-amber-900"
        data-testid={`warning-cta-${warning.code}`}
      >
        Vai al campo
      </Button>
    </div>
  );
}

